<template>
  <dialog-scroll-lock fullscreen persistent no-click-animation transition="" :value="true">
  <div class="grey lighten-4 fill-height d-flex flex-column">

    <error-dialog :status="loadSpreadsheetStatus" />
    <error-dialog :status="loadContactsStatus" />
    <error-dialog :status="createNextStatus" />
    <error-dialog :status="saveStatus" />
    <error-dialog :status="copyAutomationStatus" />
    <error-dialog :status="deleteAutomationStatus" />
    <error-dialog :status="loadPriceStatus" />

    <sign-in-dialog ref="sign-in" v-model="showSignIn" />

    <input
      v-show="false"
      ref="file-upload"
      type="file"
      accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
      @change="loadSpreadsheet"
    />

    <card-open v-if="showPreview" :preview="selectedRowPreview" @close="showPreview = false" />

    <select-designs-dialog
      v-if="showSelectDesigns"
      :limit="100"
      v-model="staged.design_ids"
      @input="showSelectDesigns = false"
    />

    <add-payment-method-dialog
      v-if="showAddPaymentMethod"
      show-name
      v-model="showAddPaymentMethod"
      @complete="$emit('update-account')"
    />

    <dialog-scroll-lock
      v-if="showImportSpreadsheet"
      :width="showSpreadsheetOptions.includes('Example template') ? 800 : 500"
      v-model="showImportSpreadsheet"
    >
      <v-card>
        <v-btn class="ma-3" style="z-index: 1; top: 0; right: 0;" absolute icon @click="showImportSpreadsheet = false">
          <v-icon v-html="mdiClose" />
        </v-btn>

        <v-card-title>Import from spreadsheet</v-card-title>
        <v-card-subtitle>csv, xls, xlsx</v-card-subtitle>
        <title-toggle
          class="title-toggle-card"
          multiple title="Import options" color="primary"
          :icon="mdiCog"
          v-model="showSpreadsheetOptions"
        >
          <template v-slot:title>
            <span class="text-body-2">Import options</span>
          </template>
          <div class="px-6 text-body-2">
            <template v-if="staged.order.type == 'GROUP'">
              <v-checkbox
                class="mt-0"
                hide-details label="Include contributors column"
                v-model="staged.import.contributors"
              >
                <template v-slot:label>Include <code class="mx-1">contributors</code> column</template>
              </v-checkbox>
              <div class="ml-8 mb-6 text--label-active">A list of email addresses to invite to sign each card</div>
            </template>

            <template v-if="global.profile?.org_id">
              <v-checkbox
                class="mt-0"
                hide-details label="Include owner column"
                v-model="staged.import.owner"
              >
                <template v-slot:label>Include <code class="mx-1">owner</code> column</template>
              </v-checkbox>
              <div class="ml-8 mb-6 text--label-active">Delegate ownership to another email address in your organization</div>
            </template>

            <v-checkbox
              class="mt-0"
              hide-details label="Include send_time column"
              v-model="staged.import.send_time"
            >
              <template v-slot:label>Include <code class="mx-1">send_time</code> column</template>
            </v-checkbox>
            <div class="ml-8 text--label-active">Times can be expressed as 12-hour with AM / PM or 24-hour</div>

            <v-checkbox
              class="mt-6"
              hide-details label="Include timezone column"
              v-model="staged.import.timezone"
            >
              <template v-slot:label>Include <code class="mx-1">timezone</code> column</template>
            </v-checkbox>
            <div class="ml-8 text--label-active">Must be in IATA format, e.g. {{TZ}}</div>

            <div class="mt-6 d-flex flex-wrap align-center">
              <input-time
                class="mr-3"
                style="flex: 0; min-width: 132px;"
                required dense label="Default send time"
                v-model="staged.default_send_time"
              />
              <div class="mb-2">
                <input-timezone v-model="staged.default_timezone" />
              </div>
            </div>
          </div>
        </title-toggle>
        <title-toggle
          class="title-toggle-card"
          multiple title="Example template" color="primary"
          :icon="mdiTableLarge"
          v-model="showSpreadsheetOptions"
        >
          <template v-slot:title>
            <span class="text-body-2">Example template</span>
          </template>
          <div class="px-6 overflow-auto text-body-2">
            <table class="spreadsheet-example">
              <tr>
                <th v-for="field in importFields">
                  {{field}}
                  <!-- <v-btn icon small color="primary"><v-icon small v-html="mdiHelpCircle" /></v-btn> -->
                </th>
              </tr>
              <tr v-for="row in EXAMPLE_ROWS">
                <td v-for="field in importFields" v-text="row[field] || 'Example value'" />
              </tr>
            </table>
            <v-btn class="my-3" small @click="downloadExample">
              <v-icon class="mr-2" small v-html="mdiDownload" /> Download template
            </v-btn>
          </div>
        </title-toggle>
        <v-card-actions class="justify-end">
          <v-btn color="primary" :loading="loadSpreadsheetStatus.pending" @click="$refs['file-upload'].click()">
            <v-icon class="mr-2" v-html="mdiUpload" /> Upload…
          </v-btn>
        </v-card-actions>
      </v-card>
    </dialog-scroll-lock>

    <dialog-scroll-lock
      v-if="showImportContacts"
      :width="showContactsOptions.includes('Contact properties') ? 800 : 500"
      v-model="showImportContacts"
    >
      <v-card>
        <v-btn class="ma-3" style="top: 0; right: 0;" absolute icon @click="showImportContacts = false">
          <v-icon v-html="mdiClose" />
        </v-btn>

        <v-card-title>Import from Ellacard contacts</v-card-title>
        <v-card-subtitle>
          <router-link target="_blank" :to="{ name: 'account-contacts' }">Manage your contacts</router-link>
        </v-card-subtitle>
        <title-toggle
          class="title-toggle-card"
          multiple title="Import options" color="primary"
          :icon="mdiCog"
          v-model="showContactsOptions"
        >
          <template v-slot:title>
            <span class="text-body-2">Import options</span>
          </template>
          <div class="px-6 text-body-2">
            <template v-if="staged.order.type == 'GROUP'">
              <v-checkbox
                class="mt-0"
                hide-details label="Import contributors"
                v-model="staged.import.contributors"
              >
                <template v-slot:label>Import <code class="mx-1">contributors</code></template>
              </v-checkbox>
              <div class="ml-8 mb-6 text--label-active">By default, all recipients of this automation are invited to sign every card except for the one addressed to them</div>
            </template>

            <template v-if="global.profile?.org_id">
              <v-checkbox
                class="mt-0"
                hide-details label="Import owner"
                v-model="staged.import.owner"
              >
                <template v-slot:label>Import <code class="mx-1">owner</code></template>
              </v-checkbox>
              <div class="ml-8 mb-6 text--label-active">Delegate ownership to another email address in your organization</div>
            </template>

            <v-checkbox
              class="mt-0"
              hide-details label="Import send time"
              v-model="staged.import.send_time"
            >
              <template v-slot:label>Import <code class="mx-1">send_time</code></template>
            </v-checkbox>
            <div class="ml-8 text--label-active">Times can be expressed as 12-hour with AM / PM or 24-hour</div>

            <v-checkbox
              class="mt-6"
              hide-details label="Import timezone"
              v-model="staged.import.timezone"
            >
              <template v-slot:label>Import <code class="mx-1">timezone</code></template>
            </v-checkbox>
            <div class="ml-8 text--label-active">Must be in IATA format, e.g. {{TZ}}</div>

            <div class="mt-6 d-flex flex-wrap align-center">
              <input-time
                class="mr-3"
                style="flex: 0; min-width: 132px;"
                required dense label="Default send time"
                v-model="staged.default_send_time"
              />
              <div class="mb-2">
                <input-timezone v-model="staged.default_timezone" />
              </div>
            </div>
          </div>
        </title-toggle>
        <title-toggle
          ref="select-contacts"
          class="title-toggle-card"
          multiple title="Select contacts" color="primary"
          :icon="mdiAccountMultiplePlus"
          :offset="false"
          :error="!contactRowsSelected.length"
          v-model="showContactsOptions"
        >
          <template v-slot:title>
            <span class="text-body-2">Select contacts</span>
          </template>
          <v-input class="d-none" :rules="[ v => !!v ]" :value="contactRowsSelected.length" />
          <div class="text-body-2 pl-14 pr-6">
            <div class="mb-3 d-flex align-center">
              <v-checkbox
                class="mt-n1 ml-4 mr-3"
                hide-details readonly
                :disabled="isLoadingContacts"
                :indeterminate="!!contactRowsSelected.length && (contactRowsSelected.length != contactRows.length)"
                :input-value="!!contactRowsSelected.length"
                @click="() => contactRowsSelected = (contactRowsSelected.length == contactRows.length) ? [] : [ ...contactRows ]"
              />
              <contacts-search :disabled="isLoadingContacts" :types="contactTypes" @search="v => contactSearch = v" />
            </div>
            <contacts-table
              class="flex-grow-1 overflow-auto"
              :search="contactSearch"
              :types.sync="contactTypes"
              :rows.sync="contactRows"
              :loading.sync="isLoadingContacts"
            >
              <template v-slot:item-prefix="{ item }">
                <v-checkbox
                  class="mt-0 mr-3"
                  hide-details readonly
                  :input-value="contactRowsSelected.includes(item)"
                  @click.stop="() => contactRowsSelected[ contactRowsSelected.includes(item) ? 'remove' : 'add' ](item)"
                />
              </template>
            </contacts-table>
          </div>
        </title-toggle>
        <title-toggle
          ref="contact-properties"
          class="title-toggle-card"
          multiple title="Contact properties" color="primary"
          :icon="mdiChartSankeyVariant"
          v-model="showContactsOptions"
        >
          <template v-slot:title>
            <span class="text-body-2">Contact properties</span>
          </template>
          <div class="px-6 text-body-2">
            <div class="text--label-active mb-3">Choose how to import properties from your contacts. Contacts who are missing values for those properties will <i>not</i> be imported.</div>
            <v-row>
              <v-col class="py-0" cols="12" sm="6">
                <div v-for="key in importFields" class="my-3 relative">
                  <v-input
                    class="d-none"
                    :rules="[ v => !!v ]"
                    :value="injectContactFields[key]?.contact || injectContactFields[key]?.fixed"
                  />
                  <div
                    class="absolute text--label-active ml-2 px-1 mt-n2 white"
                    v-bind:class="{
                      'error--text': !(injectContactFields[key]?.contact || injectContactFields[key]?.fixed)
                    }"
                    style="z-index: 1; border-radius: 4px;"
                  >
                    {{key}}
                  </div>
                  <div class="d-flex align-center">
                    <v-menu bottom offset-y>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          v-bind="attrs" v-on="on" v-blur
                          class="d-block fill-width pl-3 overflow-hidden"
                          style="z-index: 0; text-transform: none; letter-spacing: normal;"
                          large outlined
                          :color="(injectContactFields[key]?.contact || injectContactFields[key]?.fixed) ? 'primary' : 'error'"
                        >
                          <v-chip
                            v-if="injectContactFields[key]?.contact"
                            class="pl-1"
                            style="pointer-events: none;"
                            color="primary" small label
                          >
                            <v-icon class="mr-2" v-html="mdiCardAccountPhone" />
                            {{injectContactFields[key].contact}}
                          </v-chip>
                          <div
                            v-else-if="injectContactFields[key]?.fixed"
                            style="max-width: calc(100% - 24px); text-overflow: ellipsis; overflow: hidden; color: rgba(0, 0, 0, 0.87);"
                          >
                            {{injectContactFields[key].fixed}}
                          </div>
                          <v-spacer />
                          <v-icon class="menu-down" v-html="mdiMenuDown" />
                        </v-btn>
                      </template>
                      <v-list>
                        <div class="text--label-active px-3 mb-1">Contact properties</div>
                        <template v-for="field in getLinkableContactFields(key)">
                          <div v-if="field === FIXED_FIELD" @click.capture.stop.prevent>
                            <v-divider class="my-2" />
                            <div class="text--label-active px-3 mb-1">Set to fixed value</div>
                            <v-list-item
                              class="simple v-list-item--link"
                              :input-value="!!injectContactFields[key]?.fixed"
                            >
                              <v-icon color="primary" small v-html="mdiPencil" />

                              <v-autocomplete
                                v-if="field == 'owner'"
                                class="white"
                                dense outlined hide-details
                                :items="orgUserEmails"
                                :rules="[ v => !!v ]"
                                :value="injectContactFields[key]?.fixed"
                                @input="v => $set(injectContactFields, key, { fixed: v })"
                              />
                              <!--
                              <input-date
                                v-else-if="field == 'send_date'"
                                dense
                                :rules="[ v => !!v ]"
                              />
                              -->
                              <!--
                              <input-time
                                v-else-if="field == 'send_time'"
                                dense label="send_time"
                                :rules="[ v => !!v ]"
                              />
                              -->
                              <!--
                              <input-timezone v-else-if="field == 'timezone'" />
                              -->
                              <v-textarea
                                v-else
                                class="white"
                                dense rows="1" auto-grow hide-details outlined
                                :value="injectContactFields[key]?.fixed"
                                @input="v => $set(injectContactFields, key, { fixed: v })"
                              />
                            </v-list-item>
                          </div>
                          <v-list-item v-else class="simple" :input-value="injectContactFields[key]?.contact == field" @click="$set(injectContactFields, key, { contact: field })">
                            <v-icon color="primary" small v-html="SEARCH_FIELD_TYPES[ contactTypes[field] ]" />
                            <div>
                              <v-chip color="primary" small label class="pl-1">
                                <v-icon class="mr-2" v-html="mdiCardAccountPhone" />
                                {{field}}
                              </v-chip>
                            </div>
                          </v-list-item>
                        </template>
                      </v-list>
                    </v-menu>

                    <div v-if="$vuetify.breakpoint.smAndUp">
                      <connector
                        v-if="injectContactFields[key]?.contact"
                        :id="'icon-' + key"
                        :from="'icon-' + key"
                        :to="'contact-field-' + injectContactFields[key].contact"
                        :z-index="204"
                      />
                      &nbsp;
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6">
                <v-card outlined>
                  <div class="pa-3">
                    <div v-for="fieldType, fieldKey in contactTypes" class="py-3 relative">
                      <div class="text--label-active">
                        <span :id="'contact-field-' + fieldKey" class="ml-n2 mr-2" />
                        <span v-bind:class="{ 'primary--text': Object.entries(injectContactFields).filter(([ k ]) => importFields.includes(k)).map(([ _, v ]) => v.contact).includes(fieldKey) }">{{fieldKey}}</span>
                      </div>
                      <component
                        :is="{ address: 'ContactFieldAddress', date: 'ContactFieldDate', 'phone number': 'ContactFieldPhone' }[fieldType] || 'VTextField'"
                        class="mt-0 flex-no-wrap"
                        dense hide-details disabled
                        :value="(contactRowsSelected[0]?.contact || {})[fieldKey]"
                      >
                        <template v-slot:append-outer>
                          <v-icon small v-html="SEARCH_FIELD_TYPES[fieldType]" />
                        </template>
                      </component>
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </div>
        </title-toggle>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            :loading="isLoadingContacts || loadContactsStatus.pending"
            @click="() => [ 'select-contacts', 'contact-properties' ].every(x => $refs[x].$refs.form.validate()) ? loadContacts() : promptFix"
          >
            <v-icon class="mr-2" v-html="mdiAccountCheck" /> Load contacts
          </v-btn>
        </v-card-actions>
      </v-card>
    </dialog-scroll-lock>

    <dialog-scroll-lock
      v-if="selectedRow"
      width="500"
      :persistent="!selectedRowIsValid"
      :value="true"
      @input="selectedRow = null"
      @click:outside="updateSelectedRow(true)"
    >
      <v-card class="grey lighten-4">
        <div
          class="relative"
          style="height: 222px;"
          v-bind:style="{ 'background-color': staged.order.skin.page_background_color || '#FFF' }"
          @click="showPreview = true"
        >
          <card-background
            v-if="staged.order.skin.page_background_img"
            style="z-index: 1;"
            :src="staged.order.skin.page_background_img"
          />
          <confetti
            class="absolute-fill overflow-hidden"
            style="z-index: 2;"
            :effect="staged.order.skin.open_effect"
            :active="true"
          />
          <div
            v-if="selectedRowPreview"
            class="relative mx-auto pa-3 d-flex align-center"
            style="z-index: 3; max-width: 320px;"
          >
            <envelope
              class="ma-3"
              style="flex: 1; width: 50%; aspect-ratio: 1;"
              :from="selectedRowPreview.info.from"
              :to="selectedRowPreview.info.to"
              :skin="selectedRowPreview.skin"
            />
            <card-preview
              class="ma-3 v-card v-sheet elevation-6"
              style="flex: 1; width: 50%;"
              mode="view"
              :card="selectedRowPreview"
            />
          </div>
          <div class="preview-section btn-tooltip-container d-flex align-center justify-center overflow-visible" style="z-index: 5;">
            <div class="btn-tooltip center prominent d-flex align-center justify-center"><v-icon class="mr-2" small color="white" v-html="mdiArrowExpandAll" /> Preview as recipient…</div>
          </div>
        </div>

        <v-divider />

        <v-card-text class="overflow-scroll pt-6" style="max-height: calc(90vh - 222px - 1px - 1px - 52px);">
          <div
            v-if="getNextCreated(selectedRow)"
            class="pb-6 text--label-active font-italic warning--text d-flex align-center"
          >
            <v-icon class="mr-2" color="warning" v-html="mdiAlert" />
            <div>
              Next card already created {{getNextCreated(selectedRow).created_at | moment}} –
              <router-link :to="{ name: 'account-orders', params: { orderId: getNextCreated(selectedRow).order_id } }">open card</router-link>
            </div>
          </div>

          <v-form ref="row-form" @submit.prevent v-model="selectedRowIsValid">
          <v-row>
            <v-col v-for="key in customFields" :key="key" cols="12">
              <v-textarea
                class="white inline-error"
                dense outlined persistent-placeholder auto-grow rows="1" persistent-hint
                :disabled="NON_MODIFIABLE_KEYS.has(key)"
                :hint="NON_MODIFIABLE_KEYS.has(key) ? 'Format of this field is not editable' : null"
                :label="key"
                :rules="[ v => !!v || 'Required' ]"
                v-model="selectedRow[key]"
              >
                <template v-slot:label>
                  <span class="white">{ {{key}} }</span>
                </template>
              </v-textarea>
            </v-col>
            <v-col cols="12">
              <email-phone-field
                required label="Email address / phone number"
                :rules="[
                  v => {
                    if (!v)
                      return true;
                    if ((selectedRowI != -1) && (v.toLowerCase() == staged.rows[selectedRowI].recipient.toLowerCase()))
                      return true;
                    if (!rowsByRecipientLower[ v.toLowerCase() ])
                      return true;
                    return 'Duplicate recipient';
                  }
                ]"
                v-model="selectedRow.recipient"
              />
            </v-col>
            <v-col cols="12">
              <div class="d-flex flex-wrap ma-n2">
                <div class="ma-2 btn-group btn-group-horizontal d-flex flex-grow-1" style="width: 280px;">
                  <v-select
                    ref="send-date-month"
                    class="white flex-grow-1 inline-error"
                    style="width: 180px; margin-right: -1px;"
                    dense label="Send date" outlined persistent-placeholder persistent-hint
                    :hint="(selectedRow.send_date == '02-29') ? 'February 28th in non-leap years' : null"
                    :rules="sendDateRules"
                    :items="
                      Object.keys(EN_MONTHS).map(
                        (x, i) => ({
                          text: x.slice(0, 1).toUpperCase() + x.slice(1),
                          value: String(i + 1).padStart(2, '0')
                        })
                      )
                    "
                    :value="selectedRow.send_date ? selectedRow.send_date.split('-')[0] : null"
                    @input="
                      v => {
                        selectedRow.send_date = (v || '00') + '-' + (selectedRow.send_date?.split('-')[1] || '00');
                        this.$refs['send-date-day'].validate();
                      }
                    "
                  />
                  <v-select
                    ref="send-date-day"
                    class="white flex-grow-1"
                    style="width: 80px; margin-left: -1px;"
                    dense outlined hide-details persistent-placeholder
                    :rules="sendDateRules"
                    :items="
                      Array.from(
                        {
                          length: selectedRow.send_date && Object.values(EN_MONTHS)[ parseInt(selectedRow.send_date.split('-')[0]) - 1 ] || 31
                        },
                        (_, i) => ({ text: (i + 1) + getOrdinalSuffixEN(i + 1), value: i + 1 })
                      )
                    "
                    :value="selectedRow.send_date ? parseInt(selectedRow.send_date.split('-')[1]) : null"
                    @input="
                      v => {
                        selectedRow.send_date = (selectedRow.send_date?.split('-')[0] || '00') + '-' + String(v || 0).padStart(2, '0');
                        this.$refs['send-date-month'].validate();
                      }
                    "
                  />
                </div>
                <input-time
                  ref="time"
                  class="ma-2"
                  style="max-width: 106px;"
                  dense label="Time"
                  :rules="[ v => !!v || 'Required' ]"
                  v-model="selectedRow.send_time"
                />
              </div>
              <input-timezone v-model="selectedRow.timezone" />
            </v-col>
            <v-col v-if="global.profile?.org_id" style="padding-left: 10px;" cols="12">
              <v-checkbox
                class="mt-0"
                hide-details label="Delegate ownership"
                :input-value="selectedRow.owner != undefined"
                @change="() => (selectedRow.owner == undefined) ? $set(selectedRow, 'owner', '') : $delete(selectedRow, 'owner')"
              />
              <div v-if="selectedRow.owner == undefined" class="ml-8 text--label-active">
                Delegate responsibility for managing the card after it has been ordered to someone else in your organization
              </div>
              <v-select
                v-else
                class="ml-8 mt-2 white"
                dense outlined hide-details persistent-placeholder label="Owner"
                :rules="[ v => !!v ]"
                :items="orgUserEmails"
                v-model="selectedRow.owner"
              />
            </v-col>
            <v-col v-if="staged.order.type == 'GROUP'" cols="12">
              <div class="text--label-active">
                Group signing contributors ({{selectedRowContributorsImplicit.length + selectedRowContributorsExplicit.length}})
              </div>
              <div v-for="target in selectedRowContributorsImplicit" class="mt-2 d-flex align-center">
                <email-phone-field class="flex-grow-1 mr-3" disabled :value="target" />
                <v-btn disabled icon color="warning">
                  <v-icon v-html="mdiMinus" />
                </v-btn>
              </div>
              <template v-if="!staged.all_contributors">
                <div v-for="target, i in selectedRowContributorsExplicit" class="mt-2 d-flex align-center">
                  <email-phone-field
                    class="flex-grow-1 mr-3"
                    required
                    :rules="[
                      v => !v || !selectedRow.recipient || (v.toLowerCase() != selectedRow.recipient.toLowerCase()) || 'Cannot sign own card',
                      v => !v || !selectedRowContributorsImplicit.find(x => v.toLowerCase() == x.toLowerCase()) || 'Already a permanent contributor',
                      v => !v || !selectedRowContributorsExplicit.find((x, j) => (j != i) && (v.toLowerCase() == x.toLowerCase())) || 'Duplicate'
                    ]"
                    v-model="selectedRowContributorsExplicit[i]"
                  />
                  <v-btn
                    icon color="warning"
                    @click="selectedRowContributorsExplicit.splice(i, 1); $refs['row-form'].validate();"
                  >
                    <v-icon v-html="mdiMinus" />
                  </v-btn>
                </div>
                <div class="mt-2 text-right">
                  <v-btn icon color="primary" @click="selectedRowContributorsExplicit.push('')">
                    <v-icon v-html="mdiPlus" />
                    <span class="btn-tooltip left" v-bind:class="{ persistent: !getNContributors(selectedRow) }">
                      Add contributor
                    </span>
                  </v-btn>
                </div>
              </template>
            </v-col>
          </v-row>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <template v-if="selectedRowI == -1">
            <v-spacer />
            <v-btn @click="selectedRow = null">Cancel</v-btn>
            <v-btn color="primary" @click="updateSelectedRow(true)">Add</v-btn>
          </template>
          <template v-else>
            <v-btn class="px-2" style="min-width: unset;" @click="deleteSelectedRow">
              <v-icon color="warning" v-html="mdiDelete" />
              <span class="btn-tooltip top">Delete</span>
            </v-btn>
            <v-btn class="px-2" style="min-width: unset;" :disabled="!selectedRow.recipient" @click="showSelectedRowHistory = true">
              <v-icon v-html="mdiHistory" />
              <span class="btn-tooltip top">Automation history</span>
            </v-btn>
            <v-btn class="px-2" style="min-width: unset;" :loading="createNextStatus.pending" @click="createNext">
              <v-icon v-html="mdiCartArrowRight" />
              <span class="btn-tooltip top">Create next card</span>
            </v-btn>
            <v-spacer />
            <v-btn color="primary" @click="updateSelectedRow(true)">Close</v-btn>
          </template>
        </v-card-actions>

      </v-card>
    </dialog-scroll-lock>

    <dialog-scroll-lock v-if="showSelectedRowHistory" width="520" v-model="showSelectedRowHistory">
      <v-card class="grey lighten-4 py-2">
        <v-data-table
          class="row-pointer transparent"
          no-data-text="No previous orders found"
          must-sort sort-desc sort-by="order.created_at"
          :items-per-page="50"
          :footer-props="{ disableItemsPerPage: true, itemsPerPageOptions: [ 50 ] }"
          :headers="HISTORY_HEADERS"
          :items="selectedRowHistory"
          :loading="loadSelectedRowHistoryStatus.pending"
          @click:row="x => $router.push({ name: 'account-orders', params: { orderId: x.order_id } })"
        >
          <template v-slot:header.action>
            <v-btn icon @click="showSelectedRowHistory = false">
              <v-icon v-html="mdiClose" />
            </v-btn>
          </template>
          <template v-slot:item.order="{ value }">
            <v-lazy class="my-1 my-sm-2" style="aspect-ratio: 5 / 7; width: 60px;">
              <account-order-preview
                mode="view"
                :width="60"
                :order="value"
                :is-card-owner="value.user_id == globalUserId"
              />
            </v-lazy>
          </template>
          <template v-slot:item.order.created_at="{ value }">
            {{value | moment('short')}}
          </template>
          <template v-slot:item.order.automation_send_at="{ item, value }">
            <div v-bind:class="{ 'text-decoration-line-through': item.order.info.send_at != value }">
              {{value | moment('short')}}
            </div>
            <div v-if="item.order.info.send_at && (item.order.info.send_at != value)">
              {{item.order.info.send_at | moment('short')}}
            </div>
          </template>
          <template v-slot:item.action>
            <v-btn class="hidden-xs-only" icon color="primary">
              <v-icon v-html="mdiChevronRight" />
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
    </dialog-scroll-lock>

    <div class="flex-grow-0 pa-3 elevation-6 overflow-visible d-flex align-center" style="z-index: 2;">

      <v-btn
        v-if="$vuetify.breakpoint.smAndDown && $route.hash"
        exact icon color="ec-dark-blue"
        :to="$routeAssign({ hash: undefined })"
      >
        <v-icon v-html="mdiChevronLeft" />
      </v-btn>
      <v-btn
        v-else-if="$vuetify.breakpoint.mdAndUp"
        class="pl-2"
        exact text color="ec-dark-blue"
        :to="back ? $routeAssign({ hash: '#' + back }) : routeOnClose"
      >
        <v-icon v-html="mdiChevronLeft" /> Back
      </v-btn>

      <div
        class="mx-auto ec-dark-blue--text title font-weight-bold"
        style="white-space: nowrap; text-overflow: ellipsis; overflow: hidden;"
      >
        <template v-if="step">
          <v-icon color="ec-dark-blue" v-html="step.icon" />
          {{step.name}}
        </template>
        <template v-else>Automation</template>
      </div>

      <v-btn icon color="ec-dark-blue" exact :to="routeOnClose">
        <v-icon v-html="mdiClose" />
      </v-btn>
    </div>

    <div v-if="loadStatus.pending" class="flex-grow-1">
      <v-progress-linear indeterminate />
    </div>

    <div v-else-if="!staged" class="flex-grow-1 d-flex flex-column justify-center align-center text-center pa-4">
      <div class="title warning--text">Automation not found</div>
      <div class="mt-12 text-body-2">
        Think you're seeing this page in error? Please let us know!<br>
        <a href="mailto:support@ellacard.com">support@ellacard.com</a>
      </div>
    </div>

    <!-- Note that we adjust overflow for the 'sign' component to allow the buttons to overflow vertically -->
    <div v-else class="flex-grow-1 d-flex relative" style="z-index: 1;">
      <v-slide-x-transition>
        <div
          v-if="$vuetify.breakpoint.mdAndUp || !$route.hash"
          class="grey lighten-3 overflow-auto fill-width d-md-flex flex-md-column"
          v-bind:style="$vuetify.breakpoint.mdAndUp ? { 'min-width': '300px', 'max-width': '300px' } : {}"
        >

          <div class="pt-6 px-3 pb-3 overflow-auto" style="flex: none; white-space: nowrap;">
            <v-lazy
              v-for="id, i in staged.design_ids.length ? staged.design_ids : [ blankDesignId ]" :key="'left-' + id"
              class="d-inline-block"
              style="aspect-ratio: 5 / 7; width: 80px; flex: none;"
            >
              <card-preview
                class="v-card v-sheet"
                v-bind:style="{
                  transform: (staged.design_ids.length > 1) ? `rotate(${ (1 + (i % 2) * -2) * 5 }deg)` : null
                }"
                mode="view"
                :width="80"
                :card="stagedDesigns[i] || blankDesign"
              />
            </v-lazy>
          </div>

          <div>
            <div class="my-4 ml-1 mr-2 relative btn-tooltip-container d-flex justify-center">
              <v-textarea
                ref="name"
                class="border-focus"
                v-bind:class="{ white: showEditName }"
                label="Automation name" dense outlined auto-grow rows="1" hide-details persistent-placeholder
                :rules="[ v => !!v ]"
                :readonly="!showEditName"
                v-model="staged.name"
                @focus="showEditName = true"
                @blur="showEditName = false; clearSelection(); isSignedIn && (staged.name != loaded.name) && save();"
                @keydown.enter.prevent="$refs.name.blur()"
              />
              <v-btn
                style="top: calc(100% - 8px); right: -4px;"
                v-bind:style="showEditName ? {} : { opacity: 0, 'pointer-events': 'none' }"
                absolute small color="primary"
                :loading="saveStatus.pending"
              >
                {{(isSignedIn && (staged.name != loaded.name)) ? 'Save' : 'Done'}}
              </v-btn>
              <span v-if="!showEditName" class="mt-n5 ml-3 btn-tooltip bottom right">Edit automation name</span>
            </div>

            <!--
            <div class="mt-6 mb-1 text--label-active">
              Automation is <b v-if="staged.running" class="success--text">running</b><b v-else class="warning--text">not running</b>
            </div>
            <v-btn
              small
              :color="staged.running ? 'warning' : 'primary'"
              :loading="toggleRunningStatus.pending"
              @click="toggleRunning"
            >
              <v-icon class="mr-2" small v-html="staged.running ? mdiPause : mdiPlay" />
              {{staged.running ? 'Pause' : 'Start'}} automation
            </v-btn>
            -->

            <div class="mt-6 mx-4">
              <!-- <div class="text--label-active" style="margin-top: -6px;">Next order creation date</div> -->
              <div v-if="loaded.state == 'ACTIVE'" style="color: rgb(0, 0, 0, .87);">
                <div class="text--label-active" style="margin-top: -11px;">Next order creation date</div>
                <div class="d-flex align-center">
                  <template v-if="staged.next_at">{{staged.next_at | moment('short')}}</template>
                  <template v-else>–</template>
                  <v-spacer />
                  <v-btn class="ml-1 my-n2" icon color="warning" :loading="saveStatus.pending" @click="pause">
                    <v-icon v-html="mdiPause" />
                    <span class="btn-tooltip bottom left">Pause automation</span>
                  </v-btn>
                </div>
              </div>
              <template v-else>
                <v-btn
                  v-if="loaded.last_error"
                  class="pl-2"
                  small color="warning"
                  @click="
                    $confirm({
                      title: 'Automation error',
                      msg: `This automation has been automatically paused because it encountered an error last time it tried to create an order. Please fix the problem first, then restart the automation.<br><br>Failed: ${$options.filters.moment(loaded.last_error.at)}<br>Error code: ${loaded.last_error.error}<br>Recipient: ${loaded.last_error.row?.recipient || '?'}`,
                      buttons: [ 'Cancel', 'Start automation' ],
                      primary: 'Cancel'
                    }).then(x => (x == 'Start automation') && start())
                  "
                >
                  <v-icon class="mr-2" v-html="mdiAlert" />
                  Start automation
                </v-btn>
                <v-btn v-else class="pl-2" small color="primary" :loading="saveStatus.pending" @click="start">
                  <v-icon class="mr-2" v-html="mdiPlay" />
                  Start automation
                </v-btn>
              </template>
              <div v-if="expired" class="mt-6 d-flex align-center" style="max-width: 100%;">
                <div class="text--label-active error--text">
                  Your version of this automation is likely out of date – please reload
                </div>
                <v-btn class="ml-1 my-n2 pulse" icon color="primary" @click="$router.go()">
                  <v-icon style="margin-right: -2px;" v-html="mdiReload" />
                  <span class="btn-tooltip bottom left">Reload page</span>
                </v-btn>
              </div>
            </div>
          </div>

          <div class="flex-grow-1 pt-12 pb-6 d-flex flex-column ec-dark-blue--text">
            <div class="mb-12">
              <v-list-item
                v-for="step in steps" :key="step.key"
                class="simple li-pop pl-4"
                v-bind:class="{ 'error--text': staged.dirty.includes(step.key) && step.error }"
                :to="$routeAssign({ hash: '#' + step.key })"
              >
                <v-icon v-html="step.icon" />
                <div>{{step.name}}</div>
                <v-icon v-if="staged.dirty.includes(step.key)" v-html="step.error ? iconPathExclamation : mdiCheck" />
                <v-icon v-else-if="$vuetify.breakpoint.smAndDown" v-html="mdiChevronRight" />
              </v-list-item>
            </div>

            <v-spacer />

            <div v-if="id" class="pb-12 pb-md-0">
              <v-list-item class="simple py-2 mb-n1" :aria-expanded="String(showMore)" @click="showMore = !showMore">
                <div class="d-flex align-center">
                  <div class="text--label-active">More</div>
                  <v-icon class="menu-down" v-html="mdiMenuDown" />
                </div>
              </v-list-item>
              <v-expand-transition>
                <div v-if="showMore">
                  <v-list-item class="simple li-pop pl-4" @click="copyAutomation">
                    <v-progress-circular v-if="copyAutomationStatus.pending" style="transform: none !important;" indeterminate size="24" width="2" />
                    <v-icon v-else v-html="mdiContentCopy" />
                    <div>Copy to new automation</div>
                  </v-list-item>
                  <v-list-item class="simple li-pop pl-4 warning--text" :loading="true" @click="deleteAutomation">
                    <v-progress-circular v-if="deleteAutomationStatus.pending" style="transform: none !important;" indeterminate size="24" width="2" />
                    <v-icon v-else v-html="mdiDelete" />
                    <div>Delete automation…</div>
                  </v-list-item>
                </div>
              </v-expand-transition>
            </div>
          </div>
        </div>
      </v-slide-x-transition>

      <v-divider class="hidden-sm-and-down" style="z-index: 2;" vertical />

      <div
        v-if="$vuetify.breakpoint.mdAndUp || $route.hash"
        class="flex-grow-1 fill-height overflow-auto"
        v-bind:class="{ 'absolute-fill': $vuetify.breakpoint.smAndDown }"
        style="z-index: 1;"
      >

        <div v-if="$route.hash == '#type'">
          <title-toggle
            title="What type of cards do you want to automate?"
            :icon="steps.find(x => x.key == 'type').icon"
            v-model="showType"
          >
            <v-radio-group class="mt-0" hide-details v-model="staged.order.type">
              <v-radio class="mt-0" label="Personal cards" value="PERSONAL">
                <template v-slot:label>
                  <div class="mr-6">
                    <div>Personal cards</div>
                    <div class="text--label-active">Signed by you only</div>
                  </div>
                  <v-spacer />
                  <v-icon v-html="iconPathCardPersonal" />
                </template>
              </v-radio>
              <v-radio class="mt-3" label="Group cards" value="GROUP">
                <template v-slot:label>
                  <div class="mr-6">
                    <div>Group cards</div>
                    <div class="text--label-active">Invite contributors to sign</div>
                  </div>
                  <v-spacer />
                  <v-icon v-html="iconPathCardGroup" />
                </template>
              </v-radio>
            </v-radio-group>
          </title-toggle>

          <title-toggle ref="type:designs" title="What card design(s) do you want to use?" :icon="mdiImageAlbum" v-model="showType">
            <v-input class="d-none" :rules="[ v => !!v.length ]" :value="staged.design_ids" />
            <div>If you select multiple card designs, we'll distribute them across all the recipients so everyone doesn't get the same card. Note that some designs have editable text or image slots – make sure you update those in the <router-link :to="{ hash: '#design' }">design step</router-link> if needed.</div>
            <div class="my-3">
              <v-btn small color="primary" @click="showSelectDesigns = true">
                <v-icon class="mr-2" small v-html="mdiCards" />Select design(s) from catalog…
              </v-btn>
            </div>
            <v-row class="my-0">
              <v-col v-for="id, i in staged.design_ids" :key="'type-' + id" class="relative" cols="6" sm="3">
                <v-btn style="z-index: 1; top: 0; right: 0;" absolute fab x-small @click="staged.design_ids.splice(i, 1)">
                  <v-icon small color="warning" v-html="mdiDelete" />
                  <span class="btn-tooltip bottom left">Remove this cover</span>
                </v-btn>
                <v-lazy style="aspect-ratio: 5 / 7;">
                  <card-preview
                    v-if="stagedDesigns[i]"
                    class="v-card v-card--hover v-sheet"
                    mode="edit"
                    :card="stagedDesigns[i]"
                    @click.native="curDesignId = id; $router.push({ hash: '#design' });"
                  />
                </v-lazy>
              </v-col>
            </v-row>
          </title-toggle>
        </div>

        <div v-else-if="$route.hash == '#design'" class="fill-height d-flex flex-column">
          <v-progress-linear v-if="attachCurDesignIdStatus.pending" indeterminate />
          <template v-else>
            <order-stepper-card-editor
              class="overflow-auto relative"
              style="z-index: 1;"
              :order="staged.order"
              :automation="true"
            />
            <div v-if="staged.design_ids.length > 1" class="flex-grow-0 relative" style="z-index: 2;">
              <!-- <div class="absolute text--label-active grey lighten-4 pt-1 px-2 mx-3" style="bottom: 100%; margin-bottom: -1px; border-top-left-radius: 4px; border-top-right-radius: 4px; border: 1px solid rgba(0, 0, 0, 0.12) !important; border-bottom: none;">Select a cover to make edits</div> -->
              <v-divider />
              <div
                v-el-resize="({ height }) => designCoverPreviewW = 5 * (height - ($vuetify.breakpoint.xsOnly ? 8 : 16)) / 7"
                class="overflow-auto d-flex pa-1"
                style="height: 136px; max-height: 20vw;"
              >
                <card-preview
                  v-if="designCoverPreviewW"
                  v-for="id, i in staged.design_ids" :key="'design-' + id"
                  class="ma-1 ma-sm-2 v-card v-card--hover v-sheet flex-grow-0"
                  v-bind:class="{ highlight: id == curDesignId }"
                  mode="edit"
                  :width="designCoverPreviewW"
                  :card="stagedDesigns[i] || blankDesign"
                  @click.native="curDesignId = id"
                />
              </div>
            </div>
          </template>
        </div>

        <order-stepper-theme
          v-else-if="$route.hash == '#envelope'"
          ref="envelope"
          class="fill-height"
          :order="staged.order"
          :org-skin="orgSkin"
          :show-help="global.showHelp"
          :disable-multiple-recipients="true"
        />

        <div v-else-if="$route.hash == '#group-signing'">
          <title-toggle
            ref="group-signing"
            title="Group signing contributors"
            :icon="mdiEmailEdit"
            v-model="showGroupSigning"
          >
            <v-checkbox
              class="mt-0"
              label="Invite all recipients to sign every card" hide-details
              v-model="staged.all_contributors"
            />
            <div class="pl-8 text--label-active">Except for the one addressed to them</div>
            <v-checkbox
              label="Set permanent contributors" hide-details
              :input-value="!!staged.permanent_contributors.length"
              @change="v => staged.permanent_contributors = v ? [ '' ] : []"
            />
            <div class="pl-8 text--label-active">These contributors will be invited to sign every card</div>
            <div v-if="staged.permanent_contributors.length" class="pl-8" style="max-width: 360px;">
              <div v-for="_, i in staged.permanent_contributors" class="mt-2 d-flex align-center">
                <email-phone-field
                  class="flex-grow-1 mr-3 ml-n12"
                  fixed required
                  :rules="[
                    v => !v || staged.permanent_contributors.every((x, j) => (i == j) || !x || (v.toLowerCase() != x.toLowerCase())) || 'Duplicate'
                  ]"
                  v-model="staged.permanent_contributors[i]"
                />
                <v-btn icon color="warning" @click="staged.permanent_contributors.splice(i, 1)">
                  <v-icon v-html="mdiMinus" />
                </v-btn>
              </div>
              <div class="mt-2 text-right">
                <v-btn icon color="primary" @click="staged.permanent_contributors.push('')">
                  <v-icon v-html="mdiPlus" />
                  <span class="btn-tooltip left">
                    Add permanent contributor
                  </span>
                </v-btn>
              </div>
            </div>
          </title-toggle>

          <title-toggle title="Group signing options" :icon="mdiCog" v-model="showGroupSigning">
            <v-textarea
              class="white inline-error"
              outlined no-resize rows="3" persistent-placeholder label="Add a custom message for contributors"
              :rules="buildRules({ limit: 500 })"
              v-model="staged.order.msg"
            />

            <v-checkbox hide-details label="Private signing" v-model="staged.order.skin.private_pages" />
            <div class="ml-8 text--label-active">
              Prevent contributors from viewing content created by others
            </div>

            <template v-if="orgSkin.max_giftcard_value != 0">
              <v-checkbox
                hide-details label="Prevent others from adding gift cards"
                v-model="staged.order.skin.disable_contributor_giftcards"
              />

              <v-checkbox
                hide-details label="Hide gift card totals from other contributors"
                :disabled="!!staged.order.skin.private_pages"
                :input-value="!!staged.order.skin.hide_giftcard_totals || !!staged.order.skin.private_pages"
                @change="x => staged.order.skin.hide_giftcard_totals = x"
              />
              <div class="ml-8 text--label-active">
                This will also prevent other contributors from adding value to any of your gift cards
              </div>
            </template>

            <v-checkbox label="Delay inviting contributors…" hide-details readonly disabled :input-value="true" />
            <div class="ml-8 text--label-active">
              <!-- Don't invite others to sign until the specified date / time -->
              For automations, this value is taken from the <i>create orders in advance</i> setting in the <router-link :to="{ hash: 'payment' }" @click.native.capture="showPayment = [ 'Placing orders' ]">orders / payment</router-link> step
            </div>
            <v-radio-group class="ml-8 py-0 mt-3" hide-details disabled :value="true">
              <v-radio label="Days before the card is scheduled to send to the recipient" disabled :value="true">
                <template v-slot:label>
                  <span>
                    <span class="px-1 mx-n1" style="text-decoration: underline;">{{staged.n_days_in_advance | pl('day')}}</span>
                    before the card is scheduled to send to the recipient
                  </span>
                </template>
              </v-radio>
              <v-radio class="mt-2" label="Exact time…" disabled />
            </v-radio-group>
          </title-toggle>
        </div>

        <div v-else-if="$route.hash == '#delivery'">

          <title-toggle
            ref="delivery:recipients"
            title="Recipients"
            :icon="iconPathRecipient"
            :offset="false"
            v-model="showDelivery"
          >
            <template v-slot:title>
              <div>Recipients</div>
              <div v-if="scheduleRows.length" class="mx-3 btn-group btn-group-horizontal" @click.stop="showDelivery.add('Recipients')">
                <v-btn class="pl-3 pr-2" outlined rounded :input-value="showAsTable" @click="showAsTable = true">
                  <v-icon v-html="mdiTable" />
                </v-btn>
                <v-btn
                  class="pl-2 pr-3"
                  outlined rounded
                  :input-value="!showAsTable"
                  @click="showAsTable = false"
                >
                  <v-icon v-html="mdiCalendarMultiselect" />
                </v-btn>
              </div>
            </template>
            <v-input :rules="[ v => !!v.length && !v.some(x => x.errors.length) ]" :value="scheduleRows" />
            <div v-if="!scheduleRows.length" class="mt-n5 pl-10">
              <v-btn icon color="primary" @click="selectRow()">
                <v-icon v-html="mdiPlus" />
                <span class="btn-tooltip right persistent">Add recipient…</span>
              </v-btn>
              <br>
              <v-btn class="mt-2" icon color="primary" @click="showImportSpreadsheet = true">
                <v-icon v-html="mdiTableLarge" />
                <span class="btn-tooltip right persistent">Import from spreadsheet…</span>
              </v-btn>
              <br>
              <v-btn class="mt-2" icon color="primary" @click="showImportContacts = true">
                <v-icon v-html="mdiCardAccountPhone" />
                <span class="btn-tooltip right persistent">Import from Ellacard contacts…</span>
              </v-btn>
            </div>
            <div v-else-if="showAsTable" class="mt-n10 relative">
              <div class="bulk-actions-container d-flex grey lighten-4 pr-sm-4 mr-sm-n4">
                <div class="relative" style="width: 36px; height: 36px;">
                  <btn-search class="absolute" style="right: 0;" tooltip="Search recipients" v-model="filter" />
                </div>

                <v-menu bottom offset-y min-width="300">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" icon color="primary">
                      <v-icon v-html="mdiTableEdit" />
                      <span class="btn-tooltip bottom left">Bulk actions</span>
                    </v-btn>
                  </template>
                  <v-list class="primary--text text-body-2" dense>
                    <v-list-item class="simple" @click="selectRow()">
                      <v-icon v-html="mdiPlus" />
                      <div>Add recipient…</div>
                    </v-list-item>
                    <v-list-item class="simple" @click="showImportSpreadsheet = true">
                      <v-icon v-html="mdiTableLarge" />
                      <div>
                        <div>Import from spreadsheet…</div>
                        <div class="text--label-active">csv, xls, xlsx</div>
                      </div>
                    </v-list-item>
                    <v-list-item class="simple" @click="showImportContacts = true">
                      <v-icon v-html="mdiCardAccountPhone" />
                      <div>Import from Ellacard contacts…</div>
                    </v-list-item>
                    <v-list-item class="simple" @click="downloadRecipientsTable">
                      <v-icon v-html="mdiDownload" />
                      <div>
                        <div>Download</div>
                        <div class="text--label-active">csv</div>
                      </div>
                    </v-list-item>
                    <v-divider class="my-2" />
                    <v-list-item
                      class="simple warning--text"
                      @click="
                        $confirm({
                          title: 'Are you sure?',
                          msg: 'Are you sure you want to remove all the recipients of this automation?'
                        }).then(r => { if (r == 'Yes') staged.rows = []; })
                      "
                    >
                      <v-icon v-html="mdiDelete" />
                      <div>Remove all recipients</div>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>

              <v-data-table
                class="mt-2 automation-schedule-data-table row-pointer transparent"
                no-data-text="No recipients"
                must-sort sort-by="next_create"
                :items-per-page="50"
                :footer-props="{ disableItemsPerPage: true, itemsPerPageOptions: [ 50 ] }"
                :headers="headers"
                :items="scheduleRowsFiltered"
              >
                <template v-slot:item="{ item, isMobile }">
                  <tr v-bind:class="{ 'v-data-table__mobile-table-row': isMobile }" @click="selectRow(item.data)">
                    <td
                      v-for="v, i in headers.map(x => x.value?.split('.').reduce((o, i) => o[i], item))"
                      v-if="!isMobile || headers[i].text"
                      v-bind:class="{
                        'v-data-table__mobile-row': isMobile,
                        'cell-error': !isMobile && headers[i].value && item.errors.includes(headers[i].value.slice(5)),
                      }"
                    >
                      <th
                        v-if="isMobile"
                        class="flex-grow-1 text--label-active text-left"
                        v-bind:class="{
                          'error--text': isMobile && headers[i].value && item.errors.includes(headers[i].value.slice(5))
                        }"
                        style="min-height: 21px;"
                      >
                        {{headers[i].text}}
                      </th>
                      <template v-if="headers[i].value == 'next_create'">
                        {{item.next_create | moment('short', item.data.timezone)}}
                      </template>
                      <!--
                      <template v-else-if="headers[i].value == 'data.send_date'">
                        {{v | moment('long-month-date-ordinal')}}
                      </template>
                      -->
                      <template v-else-if="headers[i].value == 'data.recipient'">{{v | email-phone}}</template>
                      <template v-else-if="headers[i].value == 'action'">
                        <v-btn icon color="primary">
                          <v-icon v-html="mdiPencil" />
                        </v-btn>
                      </template>
                      <div
                        v-else
                        v-bind:class="{
                          'cell-contents-error': isMobile && headers[i].value && item.errors.includes(headers[i].value.slice(5))
                        }"
                      >
                        {{v}}
                      </div>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </div>
            <div v-else class="mt-n4 mt-sm-n7">
              <div class="mb-2 pl-4 pr-2 pr-sm-4 d-flex align-center">
                <div class="d-flex my-n1">
                  <v-select
                    class="white flex-shrink-0 mr-2"
                    style="width: 180px;"
                    dense outlined hide-details
                    :items="Object.keys(EN_MONTHS).map((x, i) => ({ text: x.slice(0, 1).toUpperCase() + x.slice(1), value: i }))"
                    :value="parseInt(calendarFocus.split('-')[1]) - 1"
                    @input="
                      v => {
                        const parts = calendarFocus.split('-');
                        parts[1] = String(v + 1).padStart(2, '0');
                        calendarFocus = parts.join('-');
                      }
                    "
                  />
                  <v-select
                    class="white flex-shrink-0"
                    style="width: 108px;"
                    dense outlined hide-details
                    :items="eventYears"
                    :value="calendarFocus.split('-')[0]"
                    @input="
                      v => {
                        const parts = calendarFocus.split('-');
                        parts[0] = v;
                        calendarFocus = parts.join('-');
                      }
                    "
                  />
                </div>
                <!-- <div class="text--label-active font-weight-bold black--text mr-3">{{$refs.calendar?.title}}</div> -->
                <v-spacer />
                <!-- <v-btn icon color="primary" @click="$refs.calendar.prev()">
                  <v-icon v-html="mdiChevronLeft" />
                  <span class="btn-tooltip bottom">Previous month</span>
                </v-btn> -->
                <!-- <v-btn icon color="primary" :disabled="!calendarFocus" @click="calendarFocus = null">
                  <v-icon v-html="mdiCalendarToday" />
                  <span class="btn-tooltip bottom">Today</span>
                </v-btn> -->
                <!-- <v-btn icon color="primary" @click="$refs.calendar.next()">
                  <v-icon v-html="mdiChevronRight" />
                  <span class="btn-tooltip bottom">Next month</span>
                </v-btn> -->

                <v-menu bottom offset-y min-width="300">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" icon color="primary">
                      <v-icon v-html="mdiTableEdit" />
                      <span class="btn-tooltip bottom left">Bulk actions</span>
                    </v-btn>
                  </template>
                  <v-list class="primary--text text-body-2" dense>
                    <v-list-item class="simple" @click="selectRow()">
                      <v-icon v-html="mdiPlus" />
                      <div>Add recipient…</div>
                    </v-list-item>
                    <v-list-item class="simple" @click="showImportSpreadsheet = true">
                      <v-icon v-html="mdiTableLarge" />
                      <div>
                        <div>Import from spreadsheet…</div>
                        <div class="text--label-active">csv, xls, xlsx</div>
                      </div>
                    </v-list-item>
                    <v-list-item class="simple" @click="showImportContacts = true">
                      <v-icon v-html="mdiCardAccountPhone" />
                      <div>Import from Ellacard contacts…</div>
                    </v-list-item>
                    <v-list-item class="simple" @click="downloadRecipientsTable">
                      <v-icon v-html="mdiDownload" />
                      <div>
                        <div>Download</div>
                        <div class="text--label-active">csv</div>
                      </div>
                    </v-list-item>
                  </v-list>
                </v-menu>

              </div>
              <div class="overflow-hidden">
                <v-sheet style="border: none;" :height="680">
                  <v-calendar
                    ref="calendar"
                    class="calendar-automation"
                    event-more event-ripple event-color="transparent"
                    :events="events"
                    v-model="calendarFocus"
                  >
                    <template v-slot:event="{ event }">
                      <v-menu v-if="event.orderId" bottom offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <v-card class="px-1 d-flex align-center" color="primary" dark hover v-on="on" v-bind="attrs">
                            <v-icon x-small color="white" v-html="mdiHistory" />
                            <div class="mx-1 flex-grow-1" style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                              {{((customFields.includes('name') && event.row.name) || event.row.recipient || '–') | email-phone}}
                            </div>
                            <v-icon class="menu-down" small color="white" v-html="mdiMenuDown" />
                          </v-card>
                        </template>
                        <v-list class="primary--text text-body-2" dense>
                          <div class="text--label-active px-3 mb-1">This order has already been created</div>
                          <v-list-item
                            class="simple" target="_blank"
                            :to="{ name: 'account-orders', params: { orderId: event.orderId } }"
                          >
                            <v-icon small v-html="mdiOpenInNew" />
                            <div>Open existing order…</div>
                          </v-list-item>
                          <v-list-item
                            class="simple"
                            @click="
                              () => {
                                const recipient = event.row.recipient.toLowerCase();
                                const row = staged.rows.find(x => x.recipient?.toLowerCase() == recipient);
                                if (row)
                                  selectRow(row);
                                else
                                 $confirm({
                                  title: 'Recipient not found',
                                  msg: 'This recipient was not found in your automation (the recipient was likely removed / updated after this order was created).<br><br>Would you like to add this recipient back to your automation?',
                                  buttons: [ 'Cancel', 'Add' ],
                                  primary: 'Add'
                                }).then(r => (r == 'Add') && selectRow(event.row));
                              }
                            "
                          >
                            <v-icon small v-html="mdiPencil" />
                            <div>Edit automation recipient…</div>
                          </v-list-item>
                        </v-list>
                      </v-menu>

                      <v-card
                        v-else
                        class="px-1 future-event"
                        v-bind:class="{ 'future-event-error': event.errors?.length }"
                        style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
                        color="white" hover outlined
                        @click="selectRow(event.row)"
                      >
                        {{((customFields.includes('name') && event.row.name) || event.row.recipient || '–') | email-phone}}
                      </v-card>
                    </template>
                  </v-calendar>
                </v-sheet>
              </div>
            </div>
          </title-toggle>

          <title-toggle ref="delivery:options" title="Delivery options" :icon="mdiCog" v-model="showDelivery">
            <v-text-field
              v-if="!orgSkin?.email_subject"
              class="white inline-error"
              dense outlined persistent-placeholder label="Customize the subject line"
              :rules="[
                v => /\S/.test(v || '') || 'Required',
                v => ((v || '').length <= 100) || `Too long (${v.length} / 100)`
              ]"
              v-model="staged.order.skin.email_subject"
            />
            <v-checkbox
              hide-details label="Allow recipients to respond to your card"
              :input-value="!staged.order.skin.disable_reply"
              @change="v => staged.order.skin.disable_reply = !v"
            />
            <div v-if="staged.order.type == 'GROUP'" class="pl-8 text--label-active">Any group signing contributors who provided their email address when signing the card will also see responses</div>
          </title-toggle>
        </div>

        <div v-else-if="$route.hash == '#payment'">
          <title-toggle ref="payment:placing" title="Placing orders" :icon="mdiCartArrowRight" v-model="showPayment">
            <div class="mb-1 text--label-active">
              Create orders this many days in advance of the scheduled send date
            </div>
            <int-field
              required
              :min="(staged.order.type == 'GROUP') ? 1 : 0"
              :max="14"
              v-model="staged.n_days_in_advance"
            />
            <div v-if="(staged.order.type == 'GROUP') && !staged.n_days_in_advance" class="mt-1 text--label-active error--text">Group cards must be scheduled in advance to give others a chance to sign</div>

            <v-checkbox
              class="mt-9"
              hide-details label="Send order confirmation emails"
              v-model="staged.send_confirmation"
            />
            <div class="ml-8 mb-9 text--label-active">
              Send yourself (or the delegated owner) a confirmation email every time an order gets placed
            </div>

            <!--
            <v-checkbox
              class="mt-9"
              hide-details label="Automatically charge saved payment method"
              :rules="[ v => !v || !!paymentMethod ]"
              v-model="staged.auto_pay"
            />
            <div class="ml-8 text--label-active">When placing an order, we'll first try to use any subscription discounts or saved credits. If your balance is exceeded, we can either pause the automation (and notify you) or automatically charge your saved payment method.</div>

            <div v-if="staged.auto_pay" class="mt-3 pl-8">
              <div v-if="paymentMethod" class="d-inline-flex align-center font-weight-bold">
                <credit-card-preview :card="paymentMethod.card" />
                <div class="ml-2 mr-3 text--label-active">{{p.description}}</div>
                <v-btn icon color="primary" :to="{ name: 'account-overview' }">
                  <v-icon v-html="mdiPencil" />
                  <span class="btn-tooltip right">Manage saved payment methods</span>
                </v-btn>
              </div>
              <v-btn v-else class="ml-n2" icon color="error" @click="showAddPaymentMethod = true">
                <v-icon v-html="mdiCreditCardPlus" />
                <span class="btn-tooltip right persistent">Add payment method</span>
              </v-btn>
            </div>
            -->

            <!--
            <v-checkbox
              class="mt-9"
              hide-details label="Pause automation"
              :input-value="staged.state == 'PAUSED'"
              @change="v => staged.state = v ? 'PAUSED' : 'ACTIVE'"
            />
            <div class="ml-8 text--label-active">
              Prevent this automation from automatically creating new orders
            </div>
            -->
          </title-toggle>

          <title-toggle
            ref="payment:payment"
            title="Payment"
            :loading="loadPriceStatus.pending"
            :icon="mdiCurrencyUsd"
            v-model="showPayment"
          >
            <div>
              <template v-if="isSignedIn">
                <div v-if="isCovered" class="d-flex align-start">
                  <v-icon class="mr-2" color="success" v-html="mdiCheckCircle" />
                  <div>The projected annual cost of this automation is covered by your current subscription / credits</div>
                </div>
                <div v-else class="d-flex align-start">
                  <v-icon class="mr-2" color="grey" v-html="mdiMinusCircle" />
                  <div>
                    <div>The projected annual cost of this automation is not covered by your current subscription / credits</div>
                    <div class="text--label-active">Additional balance required: ${{itemized?.price}}</div>
                  </div>
                </div>

                <div class="py-1" style="margin: 0 -16px 0 -48px;">
                  <v-list-item
                    class="simple primary--text"
                    style="padding: 12px 16px 12px 48px;"
                    :aria-expanded="String(showItemized)"
                    :disabled="!itemized"
                    @click="showItemized = !showItemized"
                  >
                    <v-icon class="menu-down" style="margin-right: 0 !important;" v-html="mdiMenuDown" />
                    <div>Projected annual cost breakdown</div>
                  </v-list-item>
                </div>
                <v-expand-transition>
                  <div v-if="itemized && showItemized" class="pl-8" style="width: 432px; max-width: 100%;">
                    <itemized-price
                      style="text-transform: none !important; text-align: left;"
                      :itemized="itemized"
                    />
                  </div>
                </v-expand-transition>
              </template>

              <div v-else>
                <v-btn
                  style="margin: -6px 0 -6px -16px; text-transform: none; letter-spacing: normal; font-size: 1em;"
                  text color="primary"
                  @click="promptSignIn"
                >
                  <v-icon class="mr-2" v-html="mdiAccount" />
                  Sign in
                  <v-icon class="ml-2" v-html="mdiChevronRight" />
                </v-btn>
                <div class="pl-8 text--label-active">
                  Sign in to set up payment for this automation
                </div>
              </div>

              <div class="mt-9">
                <v-btn
                  style="margin: -6px 0 -6px -16px; text-transform: none; letter-spacing: normal; font-size: 1em;"
                  text color="primary"
                  :to="{ name: 'pricing', hash: '#subscriptions' }"
                >
                  <v-icon class="mr-2" v-html="mdiCalendarSync" />
                  Add / upgrade subscription
                  <v-icon class="ml-2" v-html="mdiChevronRight" />
                </v-btn>
                <div class="pl-8 text--label-active">
                  Current subscription plan:
                  <template v-if="subscriptionStatus == 'ACTIVE'">{{account.subscription.metadata.code}} (renews {{
                  account.subscription.next_at | moment('short-date')}})</template>
                  <template v-else-if="subscriptionStatus == 'ACTIVE_CANCELLED'">{{account.subscription.metadata.code}} (expires {{account.subscription.next_at | moment('short-date')}})</template>
                  <template v-else>–</template>
                </div>
              </div>

              <div class="mt-9">
                <v-btn
                  style="margin: -6px 0 -6px -16px; text-transform: none; letter-spacing: normal; font-size: 1em;"
                  text color="primary"
                  :to="{ name: 'pricing', hash: '#pre-paid-credit' }"
                >
                  <v-icon class="mr-2" v-html="mdiCurrencyUsd" />
                  Pre-pay for discounted credit
                  <v-icon class="ml-2" v-html="mdiChevronRight" />
                </v-btn>
                <div class="pl-8 text--label-active">Current credit: <template v-if="account?.credit_money && (account.credit_money != '0.00')">${{account.credit_money}}</template><template v-else>–</template></div>
                <div v-if="account?.giftcard_credit" class="pl-8 text--label-active">Current gift card credit: ${{account.giftcard_credit}}</div>
              </div>

              <v-checkbox
                class="mt-9"
                hide-details label="Automatically charge saved payment method"
                :rules="[ v => !v || !!paymentMethod ]"
                v-model="staged.auto_pay"
              />
              <div class="ml-8 text--label-active">When placing an order, we'll first try to use any subscription discounts or saved credits. If your balance is exceeded, we can either pause the automation (and notify you) or automatically charge your saved payment method.</div>

              <div v-if="staged.auto_pay" class="mt-3 pl-8">
                <div v-if="paymentMethod" class="d-inline-flex align-center">
                  <credit-card-preview :card="paymentMethod.card" />
                  <div class="ml-2 mr-3 text--label-active">{{paymentMethod.description}}</div>
                  <v-btn icon color="primary" :to="{ name: 'account-overview' }">
                    <v-icon v-html="mdiPencil" />
                    <span class="btn-tooltip right">Manage saved payment methods</span>
                  </v-btn>
                </div>
                <v-btn
                  v-else
                  class="ml-n2"
                  icon color="error"
                  @click="
                    () => {
                      if (isSignedIn)
                        showAddPaymentMethod = true;
                      else
                        promptSignIn();
                    }
                  "
                >
                  <v-icon v-html="mdiCreditCardPlus" />
                  <span class="btn-tooltip right persistent">Add payment method</span>
                </v-btn>
              </div>

              <!--
              <div class="mt-12 text--label-active">Payment options</div>
              <div class="py-1" style="margin: 0 -16px 0 -48px;">
                <v-list-item class="simple" style="padding: 12px 16px 12px 48px;">
                  <div>
                    <div>Add an <router-link class="primary--text" :to="{ name: 'pricing', hash: '#subscriptions' }">annual subscription</router-link><template v-if="nContributors"> (with at least {{nContributors | pl('send')}})</template></div>
                    <div class="text--label-active">Or upgrade your existing plan</div>
                  </div>
                </v-list-item>
                <v-list-item class="simple" style="padding: 12px 16px 12px 48px;">
                  <div>
                    <div>Automatically charge your saved payment method</div>
                    <div class="text--label-active">Be sure to set the corresponding option in the <i>Automation options</i> above</div>
                  </div>
                </v-list-item>
                <v-list-item class="simple" style="padding: 12px 16px 12px 48px;">
                  <div>
                    <div>Pre-pay for <router-link class="primary--text" :to="{ name: 'pricing', hash: '#pre-paid-credit' }">discounted credit</router-link></div>
                    <div class="text--label-active">Contact us for amounts over $100</div>
                  </div>
                </v-list-item>
                <v-list-item class="simple" style="padding: 12px 16px 12px 48px;">
                  <div>
                    <div>Pre-pay for gift card credit</div>
                    <div class="text--label-active">Available to verified business customers only</div>
                  </div>
                </v-list-item>
                <v-list-item class="simple" style="padding: 12px 16px 12px 48px;">
                  <div>
                    <div>Contact us: <a class="primary--text" href="mailto:support@ellacard.com">support@ellacard.com</a></div>
                    <div class="text--label-active">With any questions or for additional payment options</div>
                  </div>
                </v-list-item>
              </div>
              -->
            </div>
          </title-toggle>
        </div>
      </div>

    </div>

    <div class="flex-grow-0 pa-3 elevation-6 overflow-visible d-flex justify-end" style="z-index: 2;">
      <v-btn
        v-if="$vuetify.breakpoint.smAndDown"
        v-show="!!$route.hash"
        class="pl-2"
        exact text color="ec-dark-blue"
        :to="$routeAssign({ hash: undefined })"
      >
        <v-icon v-html="mdiChevronLeft" /> Automation
      </v-btn>
      <v-btn
        v-else
        class="pl-2"
        exact text color="ec-dark-blue"
        :to="back ? $routeAssign({ hash: '#' + back }) : routeOnClose"
      >
        <v-icon v-html="mdiChevronLeft" /> Back
      </v-btn>

      <v-spacer />

      <v-btn
        v-bind:class="{ pulse: isDirty }"
        text color="ec-dark-blue"
        :disabled="!isDirty"
        :loading="saveStatus.pending"
        @click="save"
      >
        Save
      </v-btn>
      <v-btn
        v-if="$route.hash"
        class="pr-2"
        exact text color="ec-dark-blue"
        :disabled="loadStatus.pending"
        :loading="loadStatus.pending"
        :to="$routeAssign({ hash: next ? '#' + next : undefined })"
      >
        Next <v-icon v-html="mdiChevronRight" />
      </v-btn>
    </div>

  </div>
  </dialog-scroll-lock>
</template>


<style scoped>
.v-card.highlight::before {
  content: '';
  position: absolute;
  inset: -5px;
  border: 2px dashed #1976d2;
  border-radius: 4px !important;
  background-color: #1976d233;
}

.spreadsheet-example {
  min-width: 100%;
  border-collapse: collapse;
}

.spreadsheet-example th,
.spreadsheet-example td {
  text-align: left;
  padding: 4px 8px;
  border: 1px solid rgba(0, 0, 0, 0.6);
}

.spreadsheet-example th {
  white-space: nowrap;
}

.spreadsheet-example td {
  height: 69px;
}

.preview-section {
  position: absolute;
  inset: 0;
}

.preview-section:before {
  cursor: pointer;
  content: '';
  position: absolute;
  inset: 0;
  z-index: 4;
  background-color: #000;
  opacity: 0;
  transition: opacity 0.2s cubic-bezier(0.4, 0, 0.6, 1);
}

.preview-section:hover:before {
  opacity: .08;
}

.title-toggle-card {
  margin: 0 !important;
}

.bulk-actions-container {
  position: absolute;
  top: 16px;
  right: 8px;
  z-index: 1;
}
@media (min-width: 600px) {
  .bulk-actions-container {
    top: 4px;
    right: 16px;
  }
}

.future-event {
  border: 1px dashed #1976d2 !important;
  color: rgba(0, 0, 0, .87) !important;
}
.future-event-error {
  border-color: rgb(255, 82, 82) !important;
}
</style>

<style>
.v-calendar.calendar-automation .v-event {
  background-color: unset;
  overflow: visible;
}

.title-toggle-card > .title.simple {
  padding: 12px 24px;
}
.title-toggle-card > .v-form.offset {
  max-width: unset;
  margin: 0 24px 0 32px;
}

.border-focus:not(.v-input--is-focused):not(.error--text) fieldset {
  border-color: transparent;
}
</style>


<style scoped>
table.pricing > tr:first-child > td {
  border-bottom: 1px solid grey;
}
table.pricing td {
  padding: 0 8px;
}
table.pricing td:not(:last-child) {
  border-right: 1px solid grey;
}
table.pricing td:first-child {
  padding-left: 0;
}
table.pricing td:last-child {
  padding-right: 0;
}

tr.example > th {
  text-align: left;
}
</style>


<style>
.contact-property input::placeholder {
  color: rgba(0, 0, 0, .87) !important;
}
.contact-property .v-input__slot {
  font-size: 85%;
}
.contact-property .v-input__append-outer {
  cursor: default;
  margin: 0 0 0 8px;
  transition: opacity .15s;
}

.automation-schedule-data-table th {
  white-space: nowrap;
}
.automation-schedule-data-table .v-data-table-header-mobile__wrapper {
  padding-right: 80px;
}
.automation-schedule-data-table .cell-error {
  border: rgb(255, 82, 82) dashed 1px;
  box-sizing: border-box;
  background-color: rgb(255, 82, 82, .18) !important;
  width: 100%;
}
.automation-schedule-data-table .cell-error + .cell-error {
  border-left: none;
}


.automation-schedule-data-table .cell-contents-error {
  outline: rgb(255, 82, 82) dashed 1px;
  background-color: rgb(255, 82, 82, .18) !important;
  width: 80px;
  min-height: 17px;
}
</style>

<script>
import AccountOrderPreview from '@/components/AccountOrderPreview.vue';
import AddPaymentMethodDialog from '@/components/AddPaymentMethodDialog.vue';
import BtnSearch from '@/components/BtnSearch.vue';
import CardBackground from '@/components/CardBackground.vue';
import CardOpen from '@/views/CardOpen.vue';
import CardPreview from '@/components/CardPreview.vue';
import Confetti from '@/components/Confetti.vue';
import Connector from '@/components/Connector.vue';
import ContactFieldAddress from '@/components/ContactFieldAddress.vue';
import ContactFieldDate from '@/components/ContactFieldDate.vue';
import ContactFieldPhone from '@/components/ContactFieldPhone.vue';
import ContactsSearch from '@/components/ContactsSearch.vue';
import ContactsTable from '@/components/ContactsTable.vue';
import CreditCardPreview from '@/components/CreditCardPreview.vue';
import EmailPhoneField from '@/components/EmailPhoneField.vue';
import Envelope from '@/components/Envelope.vue';
import InputDate from '@/components/InputDate.vue';
import InputTime from '@/components/InputTime.vue';
import InputTimezone from '@/components/InputTimezone.vue';
import IntField from '@/components/IntField.vue';
import ItemizedPrice from '@/components/ItemizedPrice.vue';
import OrderStepperCardEditor from '@/components/OrderStepperCardEditor.vue';
import OrderStepperTheme from '@/components/OrderStepperTheme.vue';
import Price from '@/components/Price.vue';
import SelectDesignsDialog from '@/components/SelectDesignsDialog.vue';
import SignInDialog from '@/components/SignInDialog.vue';
import TitleToggle from '@/components/TitleToggle.vue';
import inputValidation from '@/mixins/input-validation.js';
import { orgSkin } from '@/mixins/org-skin.js';
import { publicDesign, blankDesignId, blankDesign } from '@/mixins/public-design.js';
import readSpreadsheet from '@/mixins/spreadsheet-reader.js';
import getBlobMediaFiles from '@/utils/card-media.js';
import downloadCSV from '@/utils/csv-download.js';
import { updateOrderDesign } from '@/utils/edit-order.js';
import { EMAIL_REGEX } from '@/utils/email.js';
import modernize from '@/utils/order-modernize.js';
import { format, getISO, getOrdinalSuffixEN } from '@/utils/time.js';
import { LOCALE, TZ } from '@/utils/intl.js';
import { getStatus } from '@/utils/subscription.js';
import iconPathCardGroup from 'raw-loader!@/assets/icon-path-card-group.txt';
import iconPathCardPersonal from 'raw-loader!@/assets/icon-path-card-personal.txt';
import iconPathCardUnknownType from 'raw-loader!@/assets/icon-path-card-unknown-type.txt';
import iconPathEnvPalette from 'raw-loader!@/assets/icon-path-env-palette.txt';
import iconPathExclamation from 'raw-loader!@/assets/icon-path-exclamation.txt';
import iconPathGroupSign from 'raw-loader!@/assets/icon-path-group-sign.txt';
import iconPathRecipient from 'raw-loader!@/assets/icon-path-recipient.txt';
import { mdiEmailFast, mdiAccountCheck, mdiCog, mdiCards, mdiClose, mdiChevronLeft, mdiChevronRight, mdiMinus, mdiPlus, mdiCardAccountPhone, mdiUpload, mdiDownload, mdiFormatTextVariant, mdiCalendarMultiselect, mdiPhone, mdiMapMarker, mdiDelete, mdiPencil, mdiAccountGroup, mdiImageAlbum, mdiMenuDown, mdiTableLarge, mdiHelpCircle, mdiTableEdit, mdiAccountEdit, mdiHistory, mdiArrowExpandAll, mdiCartArrowRight, mdiAccountPlus, mdiAccountMinus, mdiAccountStar, mdiChartSankeyVariant, mdiAccountMultiplePlus, mdiPlay, mdiPause, mdiCheck, mdiEmailEdit, mdiTable, mdiCalendarToday, mdiCart, mdiCurrencyUsd, mdiCreditCardPlus, mdiOpenInNew, mdiContentCopy, mdiCheckCircle, mdiMinusCircle, mdiCalendarSync, mdiArrowRightBold, mdiAlert, mdiReload, mdiAccount } from '@mdi/js';


const NON_MODIFIABLE_KEYS = new Set([ 'owner', 'recipient', 'send_date', 'send_time', 'timezone', 'contributors' ]);


const SEARCH_FIELD_TYPES = {
  text: mdiFormatTextVariant,
  date: mdiCalendarMultiselect,
  'phone number': mdiPhone,
  address: mdiMapMarker
};

const LINKABLE_CONTACT_FIELD_TYPES_BY_KEY = {
  recipient: [ 'phone', 'text' ],
  send_date: [ 'date' ],
  send_time: [ 'text' ],
  timezone: [ 'text' ],
  contributors: [ 'text', 'FIXED' ],
  owner: [ 'text', 'FIXED' ]
};

const FIXED_FIELD = {};


const HISTORY_HEADERS = [
  { text: 'Preview', value: 'order', sortable: false, width: '100px', fixed: true },
  { text: 'Created', value: 'order.created_at' },
  { text: 'Send date', value: 'order.automation_send_at' },
  { value: 'action', sortable: false, width: '1px', fixed: true }
];


const DESIGN_CACHE = {};


function inject(base, values) {
  const order = _.cloneDeep(base);
  for (const [ key, value ] of Object.entries(values)) {
    const re = new RegExp(`\\{\\s*${key}\\s*\\}`, 'g');
    const v = value || '';
    order.info.from = (order.info.from || '').replace(re, v);
    order.info.to = (order.info.to || '').replace(re, v);
    order.msg = (order.msg || '').replace(re, v);
    order.skin.email_subject = (order.skin.email_subject || '').replace(re, v);
    for (const page of order.pages) {
      for (const blob of page.blobs) {
        if (blob.type == 'TEXT') {
          blob.text.text = blob.text.text.replace(re, v);
        } else if (blob.type == 'BTN') {
          for (const btnKey of [ 'url', 'text' ])
            if (blob.btn[btnKey])
              blob.btn[btnKey] = blob.btn[btnKey].replace(re, v);
        } else if (blob.type == 'IMG') {
          blob.img.src = blob.img.src.replace(re, v);
        }
      }
    }
  }
  return order;
}


const EN_MONTHS = {
  january: 31,
  february: 29,
  march: 31,
  april: 30,
  may: 31,
  june: 30,
  july: 31,
  august: 31,
  september: 30,
  october: 31,
  november: 30,
  december: 31
};

const MONTHS_MAX_DAYS = [ 31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31 ];


const EXAMPLE_ROWS = [
  {
    owner: 'manager@example.com',
    name: 'Sara',
    recipient: 'sara@example.com',
    send_date: 'February 1',
    send_time: '10:30 AM',
    timezone: 'America/Los_Angeles',
    contributors: 'mark@example.com, alex@example.com, kelly@example.com'
  },
  {
    owner: 'manager@example.com',
    name: 'Mark',
    recipient: 'mark@example.com',
    send_date: 'March 13',
    send_time: '10:30 AM',
    timezone: 'America/New_York',
    contributors: 'sara@example.com, alex@example.com, kelly@example.com'
  },
  {
    owner: 'manager@example.com',
    name: 'Alex',
    recipient: 'alex@example.com',
    send_date: 'August 9',
    send_time: '10:00 AM',
    timezone: 'Europe/Madrid',
    contributors: 'sara@example.com, mark@example.com, kelly@example.com'
  },
  {
    owner: 'manager@example.com',
    name: 'Kelly',
    recipient: 'kelly@example.com',
    send_date: 'September 22',
    send_time: '5:00 PM',
    timezone: 'America/Los_Angeles',
    contributors: 'sara@example.com, mark@example.com, alex@example.com'
  }
];


function buildDefault() {
  return {
    "user_id": "-",
    "org_id": null,
    "name": "New automation",
    "dirty": [],
    "design_ids": [],
    "order": {
      "type": "PERSONAL",
      "info": {
        "to": "{ name }",
        "from": null
      },
      "pages": [],
      "skin": {
        "card_fixed_last_page": null,
        "open_effect": null,
        "music": null,
        "env_background_color": "#edf6fc",
        "env_font": "Architects Daughter",
        "env_font_color": "#28547e",
        "env_stamp": "https://cdn.ellacard.com/stamps/Ellacard.png",
        "header_logo": "https://ellacard.com/ellacard-logo-filled-307x160.png",
        "hide_account": false,
        "max_giftcard_value": 500000,
        "hide_giftcard_totals": false,
        "disable_contributor_giftcards": false,
        "page_background_color": "#FFF",
        "page_background_img": null,
        "done_url": null,
        "disable_reply": false,
        "email_from": "delivery@ellacard.com",
        "email_subject": "You received a card",
        "email_footer_logo": "https://ellacard.com/ellacard-logo-filled-307x160.png",
        "email_footer_url": "https://ellacard.com/",
        "email_unsubscribe_url": null,
        "private_pages": false
      },
      "msg": "Please sign this group card for { name }",
      "tz": TZ,
      "locale": LOCALE
    },
    "import": {
      "contributors": false,
      "owner": false,
      "send_time": false,
      "timezone": false
    },
    "all_contributors": false,
    "permanent_contributors": [],
    "default_send_time": "09:30:00",
    "default_timezone": TZ,
    "n_days_in_advance": 3,
    "send_confirmation": true,
    "auto_pay": false,
    "rows": [],
    "state": "PAUSED",
    "next_at": null
  };
}


function dedupe(x, target) {
  const o = Object.values(Object.fromEntries(x.filter(y => y).map(y => [ y.toLowerCase(), y ])));
  if (!target)
    return o;
  const lower = target.toLowerCase();
  return o.filter(x => x.toLowerCase() != lower);
}


function buildHistoryByRecipientLower(history) {
  const o = {};
  for (const [ order_id, created_at, send_at, design_id, row ] of (history || [])) {
    if (send_at) {
      const r = row.recipient.toLowerCase();
      if (!o[r] || (new Date(send_at) > new Date(o[r].send_at)))
        o[r] = { order_id, created_at, design_id, send_at };
    }
  }
  return o;
}


function getNextDesignID(pool, rowI, previous) {
  // Note: this logic should exactly match that in the backends payment_automation._get_design_id function, save for
  // the fact that it returns the blankDesignId instead of throwing an exception

  if (!pool.length)
    return blankDesignId;

  const notSeen = previous.length ? pool.filter(x => !previous.includes(x)) : pool;
  if (notSeen.length)
    return notSeen[ rowI % notSeen.length ];

  // We've seen every design, let's choose the one we haven't seen in the longest time
  const filtered = new Set(pool);
  for (const designId of previous) {
    if (filtered.size == 1)
      break;
    filtered.remove(designId)
  }
  return filtered.values().next().value;
}


function addDays(d, n) {
  // Note that we deliberately use setTime instead of setDate to avoid issues with daylight savings across differing
  // timezones and to match the logic of the backend's 'send_at_contributors_delta_s' parameter.
  d = new Date(d);
  d.setTime(d.getTime() + (n * 24 * 60 * 60 * 1000));
  return d;
}


export default {
  name: 'account-automation',

  routerViewMeta: {
    $hide: [ 'header', 'footer' ],
    title: 'Automation'
  },

  components: {
    AccountOrderPreview,
    AddPaymentMethodDialog,
    BtnSearch,
    CardBackground,
    CardOpen,
    CardPreview,
    Confetti,
    Connector,
    ContactFieldAddress,
    ContactFieldDate,
    ContactFieldPhone,
    ContactsSearch,
    ContactsTable,
    CreditCardPreview,
    EmailPhoneField,
    Envelope,
    InputDate,
    InputTime,
    InputTimezone,
    IntField,
    ItemizedPrice,
    OrderStepperCardEditor,
    OrderStepperTheme,
    Price,
    SelectDesignsDialog,
    SignInDialog,
    TitleToggle
  },

  mixins: [ inputValidation, orgSkin, publicDesign, readSpreadsheet ],

  props: [ 'account', 'id' ],

  data() {
    return {
      loaded: null,
      staged: null,
      history: null,
      historyByRecipientLower: {},
      stagedDesigns: [],
      stagedOrder: null,
      selectedRow: null,
      selectedRowContributorsExplicit: [],
      selectedRowI: -1,
      selectedRowIsValid: false,
      selectedRowPreview: null,
      selectedRowNextSendDate: null,
      selectedRowHistory: [],
      curDesignId: null,
      filter: '',
      orgUserEmails: [],
      contactGroups: [],
      selectedContactGroupId: 'ALL',
      showSignIn: false,
      showMore: false,
      showType: [ 'What type of cards do you want to automate?' ],
      showGroupSigning: [ 'Group signing contributors' ],
      showDelivery: [ 'Recipients' ],
      showPayment: [ 'Placing orders' ],
      showSpreadsheetOptions: [],
      showContactsOptions: [ 'Contact properties' ],
      showAddPaymentMethod: false,
      showPreview: false,
      showSelectDesigns: false,
      showImportSpreadsheet: false,
      showImportContacts: false,
      showUpdateDesignatedOwner: false,
      showEditName: false,
      showAsTable: true,
      showSelectedRowHistory: false,
      designCoverPreviewW: 0,
      calendarFocus: null,
      eventYears: [],
      contactTypes: {},
      contactSearch: { groups: [], mode: 'AND', filters: [] },
      contactRows: [],
      contactRowsSelected: [],
      isLoadingContacts: false,
      injectContactFields: {
        name: { contact: 'name' },
        recipient: { contact: 'email' }
      },
      itemized: null,
      showItemized: false,
      orgSkin: {},
      expired: false,
      lastRoute: null,
      loadStatus: this.$track('load'),
      saveStatus: this.$track('save'),
      getOrgSkinStatus: this.$track('getOrgSkin'),
      loadOrgUserEmailsStatus: this.$track('loadOrgUserEmails'),
      loadSpreadsheetStatus: this.$track('loadSpreadsheet'),
      loadContactsStatus: this.$track('loadContacts'),
      createNextStatus: this.$track('createNext'),
      attachCurDesignIdStatus: this.$track('attachCurDesignId'),
      copyAutomationStatus: this.$track('copyAutomation'),
      deleteAutomationStatus: this.$track('deleteAutomation'),
      loadPriceStatus: this.$track('loadPrice'),
      loadSelectedRowHistoryStatus: this.$track('loadSelectedRowHistory')
    };
  },

  computed: {
    steps() {
      const s = this.staged;
      if (!s)
        return [];
      const t = s.order.type;
      return [
        {
          name: 'Card type',
          key: 'type',
          icon: { PERSONAL: iconPathCardPersonal, GROUP: iconPathCardGroup }[t] || iconPathCardUnknownType,
          error: !s.design_ids.length
        },
        { name: 'Design', key: 'design', icon: mdiPencil },
        {
          name: 'Envelope / theme',
          key: 'envelope',
          icon: iconPathEnvPalette,
          error: !s.order.info.from || !s.order.info.to
        },
        ...((t == 'PERSONAL') ? [] : [ { name: 'Group signing', key: 'group-signing', icon: iconPathGroupSign, error: s.permanent_contributors.some(x => !x) || (dedupe(s.permanent_contributors).length != s.permanent_contributors.length) } ]),
        {
          name: 'Delivery schedule',
          key: 'delivery',
          icon: mdiEmailFast,
          error: !s.rows.length || this.scheduleRows.some(x => x.errors.length)
        },
        {
          name: 'Orders / payment',
          key: 'payment',
          icon: mdiCartArrowRight,
          error: ((t == 'GROUP') && !s.n_days_in_advance) || (s.auto_pay && !this.paymentMethod)
        }
      ];
    },

    step() {
      return this.steps.find(x => x.key == this.$route.hash.substr(1));
    },

    back() {
      return this.step && this.steps[ this.steps.findIndex(x => this.step.key == x.key) - 1 ]?.key;
    },

    next() {
      return this.step && this.steps[ this.steps.findIndex(x => this.step.key == x.key) + 1 ]?.key;
    },

    isDirty() {
      return !this.isSignedIn || !_.isEqual(this.loaded, this.staged);
    },

    paymentMethod() {
      const a = this.account;
      return a && (a.payment_methods || []).find(x => x.id == a.primary_payment_method);
    },

    updateStagedDesignsCondition() {
      return [ this.staged?.design_ids, this.staged?.order.background ];
    },

    updateStagedDesignsDebounceCondition() {
      return this.staged?.order.pages[0];
    },

    customFields() {
      const o = this.staged.order;
      const keys = new Set();
      const addKeys = s => (s.match(/{[^}]+}/g) || []).map(x => x.slice(1, -1).trim()).map(x => x.toLowerCase()).map(x => keys.add(x));
      for (const page of o.pages) {
        for (const blob of page.blobs) {
          if (blob.for_design_id && !this.staged.design_ids.includes(blob.for_design_id))
            continue;
          if (blob.type == 'TEXT') {
            addKeys(blob.text.text);
          } else if (blob.type == 'BTN') {
            addKeys(blob.btn.url || '');
            addKeys(blob.btn.text || '');
          } else if (blob.type == 'IMG') {
            addKeys(blob.img.src || '');
          }
        }
      }
      if (o.type == 'GROUP')
        addKeys(o.msg || '');
      addKeys(o.skin.email_subject || '');
      addKeys(o.info.from || '');
      addKeys(o.info.to || '');
      return Array.from(keys);
    },

    requiredFields() {
      return [ 'recipient', 'send_date', 'send_time', 'timezone', ...this.customFields ];
    },

    importFields() {
      const def = [
        ...(this.staged.import.owner ? [ 'owner' ] : []),
        'recipient',
        'send_date',
        ...(this.staged.import.send_time ? [ 'send_time' ] : []),
        ...(this.staged.import.timezone ? [ 'timezone' ] : []),
        ...(((this.staged.order.type == 'GROUP') && this.staged.import.contributors) ? [ 'contributors' ] : [])
      ];
      const custom = this.customFields.filter(x => !def.includes(x.toLowerCase()));
      return [ ...def, ...custom ];
    },

    headers() {
      let hasOwner = false;
      const errors = new Set();
      for (const row of this.scheduleRows) {
        if (this.global.profile?.org_id && row.data.owner)
          hasOwner = true;
        for (const e of row.errors)
          errors.add(e);
      }

      return [
        { text: 'Next order date', value: 'next_create' },
        // { text: 'Date', value: 'data.send_date' },
        ...this.customFields.map(
          x => ({ text: `{ ${x} }`, value: 'data.' + x, class: errors.has(x) ? 'error--text' : '' })
        ),
        { text: 'recipient', value: 'data.recipient' },
        ...(hasOwner ? [ {
          text: 'owner',
          value: 'data.owner',
          sort: (a, b) => ((a.errors.includes('owner') ? ' ' : '') + (a.data.owner || '')).localeCompare((b.errors.includes('owner') ? ' ' : '') + (b.data.owner || ''))
        } ] : []),
        ...((this.staged.order.type == 'GROUP') ? [ { text: '# of contributors', value: 'nContributors' } ] : []),
        { value: 'action', sortable: false, width: '72px', fixed: true }
      ];
    },

    scheduleRows() {
      const s = this.staged;
      const getErrors = row => {
        const keys = this.requiredFields.filter(y => !row[y]);
        if (this.global.profile?.org_id && row.owner && !this.orgUserEmails.includes(row.owner.toLowerCase()))
          keys.add('owner');
        return keys;
      };
      const onOrAfter = addDays(new Date(), this.staged.n_days_in_advance);
      return s.rows.map(
        (x, i) => {
          const next_send = this.getNextSendAtOnOrAfter(x, onOrAfter);
          const next_create = addDays(next_send, -this.staged.n_days_in_advance);
          return {
            key: ((x.name || '') + '\t' + this.$options.filters['email-phone'](x.recipient)).toLowerCase(),
            next_send,
            next_create,
            nContributors: this.getNContributors(x),
            errors: getErrors(x),
            data: x
          };
        }
      );
    },

    scheduleRowsFiltered() {
      const lower = (this.filter || '').toLowerCase();
      return this.scheduleRows.filter(x => x.key.includes(lower));
    },

    events() {
      const events = [];
      for (const [ orderId, createdAt, sendAt, designId, row ] of (this.history || []))
        events.push({ orderId, row, start: new Date(sendAt) });
      for (const { next_send, data, errors } of (this.staged ? this.scheduleRows : [])) {
        if (next_send && !this.getNextCreated(data))
          events.push({ row: data, errors, start: new Date(next_send) });
      }
      return events;
    },

    rowsByRecipientLower() {
      return Object.fromEntries(this.staged.rows.map(x => [ (x.recipient || '').toLowerCase(), x ]));
    },

    selectedRowContributorsImplicit() {
      return this.getImplicitContributors(this.selectedRow);
    },

    isCovered() {
      return this.itemized?.price == '0.00';
    },

    nContributors() {
      return _.sumBy(this.scheduleRows, 'nContributors');
    },

    subscriptionStatus() {
      return this.account && getStatus(this.account.subscription);
    },

    sendDateRules() {
      const s = this.selectedRow;
      return [
        v => !!s.send_date || 'Required',
        v => /^\d\d\-\d\d$/.test(s.send_date) || 'Invalid date format',
        v => {
          try {
            const [ m, d ] = s.send_date.split('-').map(x => parseInt(x));
            return ((m > 0) && (m <= 12) && (d > 0) && (d <= Object.values(EN_MONTHS)[ m - 1 ])) || 'Invalid date';
          } catch (e) {
            return 'Invalid date';
          }
        }
      ];
    },

    parsers() {
     return {
        owner: x => {
          if (!x)
            return x;
          if (!EMAIL_REGEX.test(x))
            throw new Error('invalid email address');
          if (!this.orgUserEmails.includes(x.toLowerCase()))
            throw new Error('not a member of your organization');
          return x;
        },
        recipient: x => {
          if (!x)
            throw new Error('value required');
          if (EMAIL_REGEX.test(x))
            return x;
          const digits = x.replace(/\D/g, '');
          if ((digits.length == 11 && digits.startsWith('1')) || (digits.length == 10))
            // This is a valid phone number, make sure it's formatted correctly
            return '+1' + digits.slice(-10);
          throw new Error('invalid email address / US phone number');
        },
        send_date: x => {
          if (!x)
            throw new Error('value required');
          let match, month, day;
          if (match = /^\d\d\d\d-(\d\d)-(\d\d)/.exec(x)) {
            month = parseInt(match[1]);
            day = parseInt(match[2]);
          } else if (match = /^(\d\d?)\D+(\d\d?)(\D+\d+)?$/.exec(x)) {
            const parsed = [ parseInt(match[1]), parseInt(match[2]) ];
            if (this.monthFirst)
              [ month, day ] = parsed;
            else
              [ day, month ] = parsed;
          } else if (match = /^([a-z]+)\W+(\d+)/i.exec(x)) {
            // June 1st, 2025
            month = Object.keys(EN_MONTHS).findIndex(x => x.startsWith(match[1].toLowerCase())) + 1;
            day = parseInt(match[2]);
          } else if (match = /^(\d+)[a-z]*\W+([a-z]+)/i.exec(x)) {
            // 1st June, 2025
            month = Object.keys(EN_MONTHS).findIndex(x => x.startsWith(match[2].toLowerCase())) + 1;
            day = parseInt(match[1]);
          } else {
            throw new Error('unrecognized date format');
          }
          if ((month < 1) || (month > 12))
            throw new Error('could not read month');
          if ((day < 1) || (day > MONTHS_MAX_DAYS[month - 1]))
            throw new Error('could not read day');
          return `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        },
        send_time: x => {
          if (!x)
            return x;
          const match = /^(\d\d?)\D(\d\d)\W*([ap]m)?/i.exec(x);
          if (!match)
            throw new Error('unrecognized time format');
          const h = match[1];
          const a = match[3]?.toLowerCase();
          const hInt = a ? ((a == 'pm') ? (parseInt(h) % 12 + 12) : (parseInt(h) % 12)) : parseInt(h);
          const mInt = parseInt(match[2]);
          if ((hInt < 0) || (hInt > 23))
            throw new Error('could not read hour');
          if ((mInt < 0) || (mInt > 59))
            throw new Error('could not read minute');
          return `${String(hInt).padStart(2, '0')}:${String(mInt).padStart(2, '0')}:00`;
        },
        timezone: x => {
          if (!x)
            return x;
          try {
            Intl.DateTimeFormat(undefined, { timeZone: x });
            return x;
          } catch (e) {
            throw new Error('invalid IATA timezone name');
          }
        },
        contributors: x => {
          if (!x)
            return '';
          const emails = x.split(/[",;\s]+/).filter(y => y);
          for (const email of emails)
            if (!EMAIL_REGEX.test(email))
              throw new Error(`contains invalid email address "${email}"`);
          return emails.join(',');
        }
      };
    },

    routeOnClose() {
      const l = this.lastRoute;
      return (l?.name && (l.name != 'automation')) ? l : { name: this.isSignedIn ? 'account-automations' : 'default' };
    }
  },

  created() {
    addEventListener('beforeunload', this.onBeforeUnload);
    Object.assign(this, { NON_MODIFIABLE_KEYS, EN_MONTHS, SEARCH_FIELD_TYPES, HISTORY_HEADERS, FIXED_FIELD, EXAMPLE_ROWS, LOCALE, TZ, blankDesign, format, getOrdinalSuffixEN, iconPathCardGroup, iconPathCardPersonal, iconPathEnvPalette, iconPathExclamation, iconPathGroupSign, iconPathRecipient, mdiEmailFast, mdiCalendarMultiselect, mdiAccountCheck, mdiCog, mdiCards, mdiClose, mdiChevronLeft, mdiChevronRight, mdiMinus, mdiPlus, mdiCardAccountPhone, mdiUpload, mdiDownload, mdiDelete, mdiPencil, mdiAccountGroup, mdiImageAlbum, mdiMenuDown, mdiTableLarge, mdiHelpCircle, mdiTableEdit, mdiAccountEdit, mdiHistory, mdiArrowExpandAll, mdiCartArrowRight, mdiAccountPlus, mdiAccountMinus, mdiAccountStar, mdiChartSankeyVariant, mdiAccountMultiplePlus, mdiPlay, mdiPause, mdiCheck, mdiEmailEdit, mdiTable, mdiCalendarToday, mdiCart, mdiCurrencyUsd, mdiCreditCardPlus, mdiOpenInNew, mdiContentCopy, mdiCheckCircle, mdiMinusCircle, mdiCalendarSync, mdiArrowRightBold, mdiAlert, mdiReload, mdiAccount });
  },

  beforeDestroy() {
    removeEventListener('beforeunload', this.onBeforeUnload);
    clearTimeout(this.expiredTimeout);
    clearTimeout(this.updateStagedDesignsTimeout);
  },

  beforeRouteEnter(to, from, next) {
    next(vm => vm.lastRoute = from);
  },

  async beforeRouteUpdate(to, from, next) {
    if (to.hash == from.hash)
      // We're not changing the step
      return next();

    if (this.step && !this.staged.dirty.includes(this.step.key))
      this.staged.dirty.add(this.step.key);

    if (this.step?.error) {
      const r = await this.$confirm({
        title: 'Are you sure?',
        msg: 'This step is incomplete. Are you sure you want to continue?',
        buttons: [ 'Cancel', 'Continue' ],
        primary: 'Continue'
      });
      if (r == 'Cancel')
        return next(false);
    }

    next();
  },

  async beforeRouteLeave(to, from, next) {
    if (this.isDeleted)
      return next();

    if (this.isDirty) {
      const r = await this.$confirm({
        title: 'Save changes?',
        msg: 'Do you want to save your changes?',
        buttons: [ 'Cancel', 'No', 'Yes' ]
      });
      if (r == 'Cancel')
        return;
      if (r == 'Yes')
        await this.save();
    }

    next();
  },

  methods: {
    onBeforeUnload(e) {
      if (this.isDirty)
        return e.returnValue = 'Are you sure you want leave without saving your changes?';
    },

    async promptSignIn() {
      await this.$refs['sign-in'].prompt({ subtitle: 'Please sign in to save this automation' });
      if (!this.isSignedIn)
        return;
      this.id = await this.$call('create-automation', { tz: TZ, locale: LOCALE });
      this.staged.user_id = this.globalUserId;
      this.staged.org_id = this.global.profile.org_id || null;
      await this.save();
      await this.$router.push({ name: 'account-automation', params: { id: this.id } });
    },

    clearSelection() {
      getSelection().empty();
    },

    getNextSendAtOnOrAfter(row, after) {
      let y = 2024;
      while (true) {
        let iso = getISO(`${y}-${row.send_date}`, row.send_time, row.timezone);
        if (!iso && (row.send_date == '02-29'))
          iso = getISO(`${y}-02-28`, row.send_time, row.timezone);
        if (!iso)
          return null;
        const next = new Date(iso);
        if (next > after)
          return next;
        y++;
      }
    },

    getNextSendAt(row, ignoreHistory) {
      const earliest = addDays(new Date(), this.staged.n_days_in_advance);
      const last = ignoreHistory ? null : this.historyByRecipientLower[ (row.recipient || '').toLowerCase() ];
      const after = last ? _.max([ earliest, new Date(last.send_at) ]) : earliest;
      return this.getNextSendAtOnOrAfter(row, after);
    },

    getImplicitContributors(row) {
      // Implicit contributors are the the permanent contributors plus the recipients of every card if
      // 'all_contributors' is set
      const { permanent_contributors, all_contributors, rows } = this.staged;
      return dedupe([ ...permanent_contributors, ...(all_contributors ? rows.map(x => x.recipient) : []) ], row.recipient);
    },

    getNContributors(row) {
      const { permanent_contributors, all_contributors, rows } = this.staged;
      return dedupe(
        [
          ...permanent_contributors,
          ...(all_contributors ? rows.map(x => x.recipient) : (row.contributors?.split(',') || []))
        ],
        row.recipient
      ).length;
    },

    getLinkableContactFields(key) {
      // This function should return the fields that are available to link to this 'key', i.e. the 'send_date' field
      // should only be allowed to link to contact fields of the 'date' type
      if (key == 'recipient')
        // We know there are very specific named fields that work with the 'recipient' property
        return [ 'email', 'phone' ];
      const allowed = LINKABLE_CONTACT_FIELD_TYPES_BY_KEY[key] || [ 'date', 'phone', 'text', 'FIXED' ];
      return [
        ...Object.entries(this.contactTypes).filter(x => allowed.includes(x[1])).map(x => x[0]),
        ...(allowed.includes('FIXED') ? [ FIXED_FIELD ] : [])
      ];
    },

    async load() {
      const { automation, history } = this.id ? (await this.$call('get-automation', { automation_id: this.id })) : { automation: buildDefault() };
      automation.order.state = 'PENDING';
      modernize(automation.order);

      this.loaded = _.cloneDeep(automation);
      this.staged = automation;
      this.history = history || [];
      this.historyByRecipientLower = buildHistoryByRecipientLower(history);
      this.curDesignId = automation.design_ids[0];

      await this.attachCurDesignId();

      if (automation.name == 'New automation')
        this.showEditName = true;
    },

    async selectRow(row) {
      row ||= {
        recipient: null,
        send_date: null,
        send_time: this.staged.default_send_time,
        timezone: this.staged.default_timezone
      };
      this.selectedRow = _.cloneDeep(row);
      this.selectedRowI = this.staged.rows.indexOf(row);

      if (this.selectedRowI != -1) {
        await this.$nextTick();
        this.$refs['row-form'].validate();
      }
    },

    async updateSelectedRow(close) {
      if (!this.$refs['row-form'].validate()) {
        if ([ 'recipient', 'send_date', 'send_time' ].some(x => !this.selectedRow[x]))
          return this.promptFix();

        if (close) {
          if (this.staged.rows.find((x, i) => (i != this.selectedRowI) && (x.recipient.toLowerCase() == this.selectedRow.recipient.toLowerCase())))
            return this.$confirm({ msg: 'Duplicate recipients are not allowed', buttons: [ 'Ok' ], primary: 'Ok' });

          const r = await this.$confirm({
            title: 'Are you sure?',
            msg: `This recipient contains errors – their next send date could be <i>skipped</i> if you don't edit the highlighted fields. Are you sure you're done editing?`
          });
          if (r == 'No')
            return;
        }
      }

      if (this.selectedRowContributorsExplicit.length)
        this.$set(this.selectedRow, 'contributors', this.selectedRowContributorsExplicit.join(','));
      else
        this.$delete(this.selectedRow, 'contributors');

      const i = (this.selectedRowI == -1) ? this.staged.rows.length : this.selectedRowI;
      this.staged.rows.splice(i, 1, this.selectedRow);
      if (close) {
        this.selectedRow = null
        this.selectedRowI = -1;
      }

      // Make sure we don't reset the calendar focus
      const focus = this.calendarFocus;
      await this.$nextTick();
      this.calendarFocus = focus;
    },

    async deleteSelectedRow() {
      const recipient = this.staged.rows[this.selectedRowI].recipient;
      const recipientLower = recipient.toLowerCase();

      // Excluding this row, count how many rows this person is an explicit contributor for
      let n = 0;
      for (const i in this.staged.rows) {
        if (i == this.selectedRowI)
          continue;
        if (this.staged.rows[i].contributors?.split(',').find(x => x.toLowerCase() == recipientLower))
          n++;
      }

      const pl = this.$options.filters.pl;
      if (n) {
        const confirm = {
          title: 'Remove as contributor?',
          msg: `<i>${recipient}</i> is explicitly listed as a group signing contributor for ${pl(n, 'other card')}. Do you want to remove them as a group signing contributor from ${(n == 1) ? 'that card' : 'those cards'}?`,
          buttons: [ 'Cancel', 'No', 'Yes' ]
        };
        const r = await this.$confirm(confirm);
        if (r == 'Cancel')
          return;
        if (r == 'Yes')
          for (const row of this.staged.rows)
            if (row.contributors)
              row.contributors = row.contributors.split(',').filter(x => x.toLowerCase() != recipientLower).join(',');
      } else {
        const confirm = {
          title: 'Are you sure?',
          msg: `Are you sure you want to remove <i>${recipient}</i> as an automation recipient?`
        };
        if ((await this.$confirm(confirm)) == 'No')
          return;
      }

      this.staged.rows.splice(this.selectedRowI, 1);
      this.selectedRow = null;
      this.selectedRowI = -1;

      // Make sure we don't reset the calendar focus (even though it might not actually be valid anymore)
      const focus = this.calendarFocus;
      await this.$nextTick();
      this.calendarFocus = focus;
    },

    async save() {
      if (!this.isSignedIn)
        return await this.promptSignIn();

      try {
        const s = this.staged;
        const files = await getBlobMediaFiles(s.order);
        const params = { automation_id: this.id, automation: { ...s, order: { ...s.order, files } } };
        const updated = await this.$call('update-automation', params);
        modernize(updated.order);
        this.loaded = updated;
        this.staged = _.cloneDeep(this.loaded);

        // Note that the order that comes back from the backend will be missing lots of properties, so we have to
        // re-attach the design (in particular the background)
        await this.attachCurDesignId();
      } catch (e) {
        if (e.type == 'OutOfDate')
          throw new VisibleError('Data out of date – please reload this automation');
        throw e;
      }
    },

    async start() {
      if (!this.isSignedIn)
        return this.promptSignIn();

      if (this.isDirty)
        return this.promptSave();

      const dirty = [ ...this.staged.dirty ];
      if (this.step) {
        dirty.add(this.step.key);
        await this.$nextTick();
        await this.$nextTick();
        this.$refs[this.step.key]?.$refs.form.validate();
      }

      if (this.steps.some(x => !dirty.includes(x.key))) {
        await this.$confirm({
          msg: 'Please check the settings for every step before starting the automation',
          buttons: [ 'Ok' ],
          primary: 'Ok'
        });
        if (this.$vuetify.breakpoint.smAndDown)
          this.$router.push({ hash: undefined });
        return;
      }
      if (this.steps.some(x => x.error)) {
        await this.promptFix();
        if (this.$vuetify.breakpoint.smAndDown)
          this.$router.push({ hash: undefined });
        return;
      }
      const clean = this.steps.filter(x => !this.staged.dirty.includes(x.key));
      if (clean.length) {
        const r = await this.$confirm({
          title: 'Are you sure?',
          msg: `The following steps haven't been visited:<ul>${clean.map(x => '<li>' + x.name + '</li>').join('')}</ul><br>Are you sure you want to continue?`
        });
        if (r == 'No')
          return;
      }
      this.staged.dirty = dirty;
      this.staged.state = 'ACTIVE';
      this.save();
    },

    async pause() {
      if (this.isDirty)
        return this.promptSave();
      this.staged.state = 'PAUSED';
      this.save();
    },

    getNextCreated(row) {
      const sendAt = this.getNextSendAtOnOrAfter(row, new Date());
      const created = this.historyByRecipientLower[ (row.recipient || '').toLowerCase() ];
      if (created && (new Date(created.send_at) > new Date()) && (new Date(created.send_at) <= sendAt))
        return created;
      return null;
    },

    async createNext() {
      this.updateSelectedRow(false);

      if (!this.selectedRowIsValid)
        return this.promptFix();

      if (this.isDirty)
        return this.promptSave();

      const row = this.selectedRow;
      const created = this.getNextCreated(row);
      if (created) {
        const r = await this.$confirm({
          title: 'Already created',
          msg: `The next card for this recipient has already been created. Would you like to open it?`,
          buttons: [ 'Cancel', 'Open' ],
          primary: 'Open'
        });
        if (r == 'Open')
          this.$router.push({ name: 'account-orders', params: { orderId: created.order_id } });
        return;
      }

      const send_at = this.getNextSendAtOnOrAfter(row, new Date());
      // Note that we do NOT factor in the 'n_days_in_advance' property, since it's pretty clear that the user intends
      // to create the next upcoming order, even if it's after the intended creation time.
      // const nDays = ((new Date(send_at)).getTime() - (new Date()).getTime()) / (24 * 60 * 60 * 1000);
      const r = await this.$confirm({
        title: 'Are you sure?',
        msg: `This will create the next card for this recipient right away so you can modify its details as needed. It will be scheduled to send <b>${this.$options.filters.moment(send_at)}</b>. Are you sure you want to continue?`
      });
      if (r == 'No')
        return;

      // No need to reload history if we direct you away from this page
      const params = { automation_id: this.id, recipient: row.recipient, send_at };
      const orderId = await this.$call('create-automation-order', params);
      this.$router.push({ name: 'account-orders', params: { orderId } });
    },

    attachImportedRows(rows) {
      const pl = this.$options.filters.pl;

      const nBeforeNullRemoved = rows.length;
      rows = rows.filter(x => x);
      const nNullRemoved = nBeforeNullRemoved - rows.length;

      const nBeforeExamplesRemoved = rows.length;
      rows = rows.filter(x => !x.recipient.endsWith('@example.com'));
      const nExamplesRemoved = nBeforeExamplesRemoved - rows.length;

      if (!rows.length)
        throw new VisibleError('No recipients found' + (nExamplesRemoved ? ` (filtered out ${pl(nExamplesRemoved, 'example')})` : ''));

      const nBeforeDuplicatesRemoved = rows.length;
      const seen = new Set();
      rows = rows.filter(x => seen.size < seen.add(x.recipient.toLowerCase()).size);
      const nDuplicatesRemoved = nBeforeDuplicatesRemoved - rows.length;

      const orgMaxNRows = this.orgSkin.max_n_recipients;
      const maxNRows = (!this.global.profile?.org_admin && (orgMaxNRows != undefined)) ? orgMaxNRows : 10000;
      if (rows.length > this.maxNRows)
        throw new VisibleError(`Too many recipients (maximum: ${this.maxNRows})`);

      const newRows = [];
      let nExisting = 0;
      let nUpdated = 0;
      for (const row of rows) {
        if (!row.send_time)
          row.send_time = this.staged.default_send_time;
        if (!row.timezone)
          row.timezone = this.staged.default_timezone;
        if (row.contributors)
          row.contributors = row.contributors?.split(',').filter(x => !x.endsWith('@example.com')).join(',') || '';
        const existing = this.rowsByRecipientLower[ row.recipient.toLowerCase() ];
        if (existing) {
          nExisting++;
          if (Object.entries(row).some(([ k, v ]) => v && existing[k] && (existing[k] != v))) {
            nUpdated++;
            Object.assign(existing, row);
          }
        } else {
          newRows.push(row);
        }
      }

      const msgs = [];
      if (newRows.length)
        msgs.push(`Imported ${pl(newRows.length, 'new recipient')}`);
      if (nUpdated)
        msgs.push(`Updated ${pl(nUpdated, 'existing recipient')}`);
      if (nNullRemoved)
        msgs.push(`Skipped ${pl(nNullRemoved, 'recipient')} with missing / empty values`);
      if (nExamplesRemoved)
        msgs.push(`Skipped ${pl(nExamplesRemoved, 'example recipient')}`);
      if (nDuplicatesRemoved)
        msgs.push(`Skipped ${pl(nDuplicatesRemoved, 'duplicate recipient')}`);

      this.$confirm({
        title: (newRows.length || nUpdated) ? 'Import successful' : 'No new / updated recipients to import',
        msg: msgs.length ? `<ul>${msgs.map(x => '<li>' + x + '</li>').join('')}</ul>` : undefined,
        buttons: [ 'Ok' ],
        primary: 'Ok'
      });

      this.staged.rows.push(...newRows);
    },

    async loadSpreadsheet(event) {
      try {
        await this.loadSpreadsheetKernel(event);
      } catch (e) {
        if (e instanceof VisibleError)
          throw e;
        console.error(e);
        throw new VisibleError(`We could not parse this file. Please check that the spreadsheet contains a header row and does not contain any extraneous rows / values. Please contact <a href="mailto:support@ellacard.com">support@ellacard.com</a> and attach the spreadsheet if you can't resolve this issue.`);
      }
    },

    async loadSpreadsheetKernel(e) {
      const file = e.target.files[0];

      // This is important to make sure we can upload the same file twice
      this.$refs['file-upload'].value = null;

      if (file.size / 1024 / 1024 > 1)
        throw new VisibleError('File is too large (1 MB limit)');

      await this.loadOrgUserEmailsStatus.promise;
      await this.getOrgSkinStatus.promise;

      this.monthFirst = null;
      const output = await this.readSpreadsheet(
        file,
        {
          headersCaseSensitive: false,
          headerWarnings: [
            headers => headers.includes('name') || 'Missing <code>name</code> header',
            headers => {
              if (!headers.includes('contributors'))
                return true;
              if (this.staged.order.type != 'GROUP')
                return `The <code>contributors</code> column will be ignored unless you set the automation type to <i>group</i>`;
              return true;
            }
          ],
          headerErrors: [
            headers => headers.includes('recipient') || 'Must contain <code>recipient</code> header',
            headers => headers.includes('send_date') || 'Must contain <code>send_date</code> header',
            ...[ 'contributors', 'owner', 'send_time', 'timezone' ].map(
              x => headers => !this.staged.import[x] || headers.includes(x) || `Must contain <code>${x}</code> header`
            ),
            ...this.customFields.map(
              x => headers => headers.includes(x) || `Must contain <code>${x}</code> header`
            )
          ],
          preParse: async (headers, rows) => {
            let delim;
            for (const row of rows) {
              const match = /^(\d\d?)(\D+)(\d\d?)(\D+\d+)?$/.exec((row.send_date || '').trim());
              if (!match)
                continue;
              if (parseInt(match[1]) > 12) {
                this.monthFirst = false;
                return;
              }
              if (parseInt(match[3]) > 12) {
                this.monthFirst = true;
                return;
              }
              delim = match[2];
            }
            if (delim) {
              const buttons = [ 'mm' + delim + 'dd', 'dd' + delim + 'mm' ];
              const r = await this.$confirm({
                title: 'Date order',
                msg: `Did you specify the <code>send_date</code> as <i>${buttons[0]}</i> or <i>${buttons[1]}</i>?`,
                buttons,
                primary: buttons[0]
              });
              this.monthFirst = r == buttons[0];
            }
          },
          parsers: this.parsers
        }
      );

      if (!output)
        return;

      this.attachImportedRows(output.items);
      this.showImportSpreadsheet = false;
    },

    async loadContacts() {
      // TODO: this should probably be done on the backend
      const buildRow = ({ contact }) => {
        const output = {};
        for (const key of this.importFields) {
          const field = this.injectContactFields[key];
          if (field.fixed) {
            output[key] = field.fixed;
            continue;
          }
          try {
            const value = (contact[ field.contact ] || '').trim();
            if (!value)
              // This will get reported as a contact with missing / empty values
              return null;
            output[key] = this.parsers[key] ? this.parsers[key](value) : value;
          } catch (e) {
            let msg = `<b>Import aborted</b><br>Error parsing value from contact <code>${field.contact}</code> as <code>${key}</code> – <i>${e.message}</i><div class="font-italic mt-5">${contact.name || '–'}</div><div class="font-italic caption white--text">${contact.email}${(contact.email && contact.phone) ? ', ' : ''}${contact.phone ? $options.filters['email-phone'](contact.phone) : ''}</div><div class="mt-5 text-body-2 d-inline-block" style="max-height: 69px; overflow: auto; padding: 4px 8px; border: 1px solid rgba(255, 255, 255, 0.6);">${(contact[ field.contact ] || '').trim() || '&nbsp;'}</div>`;
            throw new VisibleError(msg);
          }
        }
        return output;
      };
      this.attachImportedRows(this.contactRowsSelected.map(buildRow));
      this.showImportContacts = false;
    },

    downloadExample() {
      const headers = this.importFields;
      const rows = [ headers, ...EXAMPLE_ROWS.map(x => headers.map(y => x[y] || 'Example value')) ];
      downloadCSV(rows, 'ellacard_automation_example_template.csv');
    },

    downloadRecipientsTable() {
      const hasOwner = this.staged.rows.find(x => x.owner);
      const def = [
        ...(hasOwner ? [ 'owner' ] : []),
        'recipient',
        'send_date',
        'send_time',
        'timezone',
        ...((this.staged.order.type == 'GROUP') ? [ 'contributors' ] : [])
      ];
      const custom = this.customFields.filter(x => !def.includes(x.toLowerCase()));
      const headers = [ ...def, ...custom ];
      const rows = [ headers, ...this.staged.rows.map(x => headers.map(y => x[y])) ];
      downloadCSV(rows, (this.staged.name.replace(/[^\w -]/g, '') || '_') + '.csv');
    },

    async loadOrgUserEmails() {
      this.orgUserEmails = [];
      if (this.global.profile?.org_id)
        this.orgUserEmails = (await this.$call('list-org-user-emails')).map(x => x.toLowerCase());
    },

    async deleteAutomation() {
      const r = await this.$confirm({
        title: 'Are you sure?',
        msg: 'Are you sure you want to delete this automation?'
      });
      if (r == 'No')
        return;
      await this.$call('delete-automation', { automation_id: this.id });
      this.isDeleted = true;
      this.$router.push(this.routeOnClose);
    },

    async attachCurDesignId() {
      this.cacheDesignsLock && await this.cacheDesignsLock;
      const id = this.curDesignId || blankDesignId;
      const design = await this.getCachedDesign(id);
      if (!this.staged.order.design_id)
        updateOrderDesign(this.loaded.order, id, design);
      updateOrderDesign(this.staged.order, id, design);
    },

    async cacheDesigns(design_ids) {
      this.cacheDesignsLock && await this.cacheDesignsLock;
      this.cacheDesignsLock = new Promise(
        async r => {
          try {
            design_ids = design_ids.filter(x => !DESIGN_CACHE[x]);
            if (design_ids.length)
              Object.assign(DESIGN_CACHE, Object.fromEntries(await this.$call('get-designs', { design_ids })));
          } finally {
            r();
          }
        }
      );
    },

    async getCachedDesign(id) {
      this.cacheDesignsLock && await this.cacheDesignsLock;
      return DESIGN_CACHE[id] || await this.getPublicDesign(id);
    },

    async copyAutomation() {
      if (this.isDirty)
        return this.promptSave();

      const { tz, locale } = this.staged.order;
      const id = await this.$call('create-automation', { tz, locale });
      const automation = { ...this.staged, name: 'New automation', state: 'PAUSED' };
      try {
        await this.$call('update-automation', { automation_id: id, automation });
      } catch (e) {
        if (e.type == 'OutOfDate')
          throw new VisibleError('Data out of date – please reload this automation');
        throw e;
      }

      // Make sure to do the navigation in two phases so the view doesn't get reused (will lead to issues with 'staged'
      // not existing)
      await this.$router.push({ name: 'account-automations' });
      await this.$router.push({ name: 'account-automation', params: { id } });
    },

    promptSave() {
      return this.$confirm({ msg: 'Please save your changes before continuing', buttons: [ 'Ok' ], primary: 'Ok' });
    },

    promptFix() {
      return this.$confirm({ msg: 'Please fix the highlighted errors before continuing', buttons: [ 'Ok' ], primary: 'Ok' });
    },

    async loadPrice() {
      const params = {
        type: this.staged.order.type,
        n_cards: this.staged.rows.length,
        n_recipients: this.staged.rows.length,
        n_contributors: _.sumBy(this.scheduleRows, 'nContributors'),
        giftcards: [],
        n_giftcard_recipients: this.staged.rows.length
      };
      for (const page of this.staged.order.pages)
        for (const blob of page.blobs)
          if (blob.type == 'GIFTCARD')
            params.giftcards.push(blob.giftcard);
      if (_.isEqual(params, this.itemizedParams))
        return;
      this.itemized = null;
      if (!this.isSignedIn)
        return;
      this.itemizedParams = params;
      this.itemized = await this.$call('get-automation-projected-itemized-price', params);
    },

    async updateStagedDesigns() {
      const ids = this.staged.design_ids;
      await this.cacheDesigns(ids);
      const updated = await Promise.all(
        ids.map(
          async x => {
            const order = _.cloneDeep(this.staged.order);
            updateOrderDesign(order, x, await this.getCachedDesign(x));
            return order;
          }
        )
      );
      this.stagedDesigns = updated;
    },

    async loadSelectedRowHistory() {
      this.selectedRowHistory = [];
      if (!this.isSignedIn)
        return;
      const params = { automation_id: this.id, recipient: this.selectedRow.recipient };
      const output = await this.$call('get-automation-recipient-orders', params);
      this.selectedRowHistory = output.map(([ order_id, order ]) => ({ order_id, order }));
    }
  },

  watch: {
    '$route.hash': {
      immediate: true,
      async handler(v) {
        if (!v && this.$vuetify.breakpoint.mdAndUp)
          await this.$router.replace(this.$routeAssign({ hash: '#type' }));
        if (this.isDirty && this.isSignedIn)
          this.$nextTick(this.save);
      }
    },

    '$vuetify.breakpoint.mdAndUp'(v) {
      if (v && !this.$route.hash)
        this.$router.replace(this.$routeAssign({ hash: '#type' }));
    },

    id: {
      immediate: true,
      handler() {
        this.load();
      }
    },

    'global.profile.org_id': {
      immediate: true,
      deep: true,
      async handler(v) {
        this.orgSkin = (await this.getOrgSkin(v)) || {};
        this.loadOrgUserEmails();
      }
    },

    'staged.design_ids'(v) {
      this.cacheDesigns(v);
      if (!v.includes(this.curDesignId))
        this.curDesignId = v[0];
    },

    curDesignId() {
      this.attachCurDesignId();
    },

    updateStagedDesignsCondition: {
      deep: true,
      handler() {
        this.updateStagedDesigns();
      }
    },

    updateStagedDesignsDebounceCondition: {
      deep: true,
      handler() {
        clearTimeout(this.updateStagedDesignsTimeout);
        this.updateStagedDesignsTimeout = setTimeout(this.updateStagedDesigns, 200);
      }
    },

    selectedRow: {
      deep: true,
      async handler(v) {
        if (v) {
          this.selectedRowContributorsExplicit = v.contributors?.split(',').filter(x => x) || [];
          const order = _.cloneDeep(this.staged.order);
          const previous = _.sortBy(
            this.historyByRecipientLower[ (v.recipient || '').toLowerCase() ] || [],
            [ x => new Date(x.send_at) ]
          ).reverse();
          const created = this.getNextCreated(v);
          const i = (this.selectedRowI == -1) ? this.staged.rows.length : this.selectedRowI;
          const designId = created?.design_id || getNextDesignID(this.staged.design_ids, i, previous);
          updateOrderDesign(order, designId, await this.getCachedDesign(designId));
          this.selectedRowPreview = inject(order, v);;
          this.selectedRowNextSendDate = null;
        } else {
          this.selectedRowContributorsExplicit = [];
          this.selectedRowI = -1;
          this.selectedRowPreview = null;
          this.selectedRowNextSendDate = null;
        }
      }
    },

    showSelectedRowHistory(v) {
      v && this.loadSelectedRowHistory();
    },

    contactTypes(v) {
      if (this.injectContactFields.send_date)
        return;
      const dateFields = Object.entries(v).filter(x => x[1] == 'date').map(x => x[0]);
      if (dateFields.length == 1)
        this.injectContactFields.send_date = { contact: dateFields[0] };
    },

    contactRows(v) {
      this.contactRowsSelected = [ ...v ];
    },

    showAsTable() {
      // The 'calendar' ref does not exist right away
      this.$nextTick(() => this.$forceUpdate());
    },

    async 'step.key'(v) {
      if (this.staged.dirty.includes(v)) {
        // Empircally determined that we need to wait this many ticks
        for (let i = 0; i < 7; i++)
          await this.$nextTick();
        for (const [ key, component ] of Object.entries(this.$refs))
          if (key.startsWith(v))
            (component.$refs.form || component).validate();
      }

      if (v == 'payment')
        this.loadPrice();
    },

    'loaded.next_at'(v) {
      clearTimeout(this.expiredTimeout);
      this.expired = false;
      this.expiredTimeout = setTimeout(
        () => this.expired = this.loaded?.next_at && (new Date() >= new Date(this.loaded.next_at)),
        Math.max(0, (new Date(v)).getTime() - (new Date()).getTime() + 10000)
      );
    },

    'staged.name'(v) {
      if (v != this.loaded.name)
        this.showEditName = true;
    },

    showEditName: {
      immediate: true,
      async handler(v) {
        if (v) {
          for (let i = 0; i < 3; i++)
            await this.$nextTick();
          this.$refs.name.focus();
          if (this.staged.name == 'New automation')
            this.$refs.name.$el.querySelector('textarea').setSelectionRange(0, 14);
        }
      }
    },

    events(v) {
      const month = String((new Date()).getMonth() + 1).padStart(2, '0');
      const year = String((new Date()).getFullYear());
      let focus;
      let years;
      if (v && v.length) {
        const dates = v.map(x => x.start).map(x => format(x, 'iso-date'));
        this.eventYears = dates.map(x => x.split('-')[0]);
        focus = _.min(dates.filter(x => x > year + '-' + month));
      } else {
        this.eventYears = [ year ];
      }
      if (!focus)
        focus = year + '-' + month + '-01';
      this.calendarFocus = focus;
    }
  }
}
</script>
